// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/// <reference types="resize-observer-browser" />

import * as React from 'react';

import clsx from 'clsx';
import { Descendant } from 'slate';
import { defined, exists, mapValues, setsEqual } from '@simlin/core/common';
import { at, first, getOrThrow, last, only } from '@simlin/core/collections';
import {
  ViewElement,
  AliasViewElement,
  AuxViewElement,
  CloudViewElement,
  FlowViewElement,
  GroupViewElement,
  LinkViewElement,
  ModuleViewElement,
  StockViewElement,
  NamedViewElement,
  Point as FlowPoint,
  UID,
  LabelSide,
  StockFlowView,
  Project,
  Model,
  Rect as ViewRect,
  rectDefault as viewRectDefault,
  isNamedViewElement,
  variableHasError,
} from '@simlin/core/datamodel';
import { canonicalize } from '@simlin/core/canonicalize';

import { Alias, AliasProps } from './Alias';
import { Aux, auxBounds, auxContains, AuxProps } from './Auxiliary';
import { CLDArrow, CLDArrowPolarity } from './cld/CLDArrow';
import { CLDLoopLabel, CLDLoopPolarity } from './cld/CLDLoopLabel';
import { loopLabels } from '../loop-ranking';
import { loopRingPath, Pt } from '../loop-cycle';
import { spreadOverlaps } from '../cld-view';
import { loopColor, paperKind } from '../canvas-notation';
import { CLDNodeElement, CLDVariable, CLDVariableProps } from './cld/CLDVariable';
import { cldLabelBox, wrapLabel } from '../cld-label';
import { outwardArcPoint, perpendicularBowPoint, ringArcPoint } from '../cld-routing';
import { Cloud, cloudBounds, cloudContains, CloudProps } from './Cloud';
import { isCloudOnSourceSide, isCloudOnSinkSide } from './cloud-utils';
import { calcViewBox, displayName, labelRadii, plainDeserialize, plainSerialize, Point, Rect, screenToCanvasPoint } from './common';
import { Connector, ConnectorProps, computeLinkCreationArc, getVisualCenter } from './Connector';
import { computeLinkSeparation, SeparableLink } from '../link-separation';
import { EditableLabel } from './EditableLabel';
import { Flow, flowBounds, UpdateCloudAndFlow, UpdateFlow, UpdateStockAndFlows } from './Flow';
import { applyGroupMovement } from '../group-movement';
import { Group, groupBounds, GroupProps } from './Group';
import { Module, moduleBounds, moduleContains, ModuleProps } from './Module';
import { anyModuleHasModelReference } from '../module-warning';
import { CustomElement } from './SlateEditor';
import { Stock, stockBounds, stockContains, StockHeight, StockProps, StockWidth } from './Stock';
import { shouldShowVariableDetails } from './pointer-utils';
import {
  computeMouseDownSelection,
  computeMouseUpSelection,
  pointerStateReset,
  resolveSelectionForReattachment,
} from '../selection-logic';
import { Annotation } from '../overlay/annotations';
import { AnnotationOverlay } from '../overlay/AnnotationOverlay';
import { VariableHighlight } from '../overlay/highlights';
import { resolveHighlights } from '../overlay/resolveHighlights';
import { VariableHighlightOverlay } from '../overlay/VariableHighlightOverlay';
import { AnnotationLabel } from '../overlay/labels';
import { resolveLabels } from '../overlay/resolveLabels';
import { AnnotationLabelOverlay } from '../overlay/AnnotationLabelOverlay';
import { PatternGroup } from '../overlay/patternGroups';
import { resolvePatternGroups } from '../overlay/resolvePatternGroups';
import { PatternGroupOverlay } from '../overlay/PatternGroupOverlay';
import { isOverlayVisible, OverlayVisibility } from '../overlay/visibility';

import styles from './Canvas.module.css';

export const inCreationUid = -2;
export const fauxTargetUid = -3;
export const inCreationCloudUid = -4;
export const fauxCloudTargetUid = -5;

const fauxTarget: AuxViewElement = {
  type: 'aux',
  name: '$⁚model-internal-faux-target',
  ident: '$⁚model-internal-faux-target',
  uid: fauxTargetUid,
  var: undefined,
  x: 0,
  y: 0,
  labelSide: 'right' as LabelSide,
  isZeroRadius: true,
};

const fauxCloudTarget: CloudViewElement = {
  type: 'cloud',
  uid: fauxCloudTargetUid,
  flowUid: -1,
  x: 0,
  y: 0,
  isZeroRadius: true,
  ident: undefined,
};

function radToDeg(r: number): number {
  return (r * 180) / Math.PI;
}

// Index 6 hosts CLD-mode loop labels (D2 criterion 17) -- they sit on
// top of every variable / connector layer so the R/B badges are never
// occluded by element fills.  SFD mode leaves this layer empty.
const ZMax = 7;

// CLD loop badge radius; mirrors CLDLoopLabel's own `r` so badge de-collision
// (appendLoopLabels) reserves the same clearance the badge actually paints.
const CLD_BADGE_RADIUS = 13;

// Neutral CLD loop-ring stroke: a quiet gray so every loop reads as a closed ring
// (spec line 2) without competing with the causal arcs; the active (highlighted)
// loop overrides it with its rank colour (spec line 7).
const CLD_NEUTRAL_RING = '#94a3b8';

// Momentum scrolling physics for macOS-native feel.
// macOS apps (Finder, Safari, Maps) have snappier deceleration than iOS.
// A friction coefficient of 0.05 means velocity retains 5% after 1 second,
// giving a ~0.5-0.8 second coast for typical pan gestures.
const FRICTION_COEFFICIENT = 0.05;
const FRICTION_LOG = Math.log(FRICTION_COEFFICIENT); // ≈ -3.0

// Stop momentum when velocity drops below this threshold.
// At 60fps, 15 px/s = 0.25 px/frame - imperceptible motion.
// Lower values make the stop feel more gradual and natural.
const VELOCITY_THRESHOLD = 15;

// Pinch-to-zoom uses exponential scaling for natural feel.
// A divisor of 100 means cumulative deltaY of ~100 results in 2x zoom.
// This matches native macOS apps like Maps and Preview.
const PINCH_ZOOM_DIVISOR = 100;

// MIN_ZOOM matches the 0.2 floor used in render() to avoid mismatch between
// view state and actual rendering (which clamps zoom < 0.2 to 1.0)
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5.0;

// Tracked pointer for multi-touch pinch detection
interface TrackedPointer {
  id: number;
  x: number;
  y: number;
  timestamp: number;
}

// Velocity tracking for momentum
interface VelocityTracker {
  positions: Array<{ x: number; y: number; timestamp: number }>;
}

interface CanvasState {
  isMovingCanvas: boolean;
  isDragSelecting: boolean;
  isEditingName: boolean;
  isMovingArrow: boolean;
  isMovingSource: boolean;
  isMovingLabel: boolean;
  labelSide: 'right' | 'bottom' | 'left' | 'top' | undefined;
  editingName: Array<Descendant>;
  editNameOnPointerUp: boolean;
  flowStillBeingCreated: boolean;
  dragSelectionPoint: Point | undefined;
  moveDelta: Point | undefined;
  movingCanvasOffset: Point | undefined;
  initialBounds: ViewRect;
  svgSize: Readonly<{ width: number; height: number }> | undefined;
  inCreation: ViewElement | undefined;
  inCreationCloud: CloudViewElement | undefined;
  // Multi-touch pinch state
  isPinching: boolean;
  initialPinchDistance: number;
  initialPinchZoom: number;
  // Store the MODEL coordinates of the point under the initial pinch center.
  // This is the fixed point that should stay under the user's fingers during zoom.
  pinchModelPoint: Point | undefined;
  // Which segment of a flow is being dragged (undefined = valve)
  draggingSegmentIndex: number | undefined;
}

export interface CanvasProps {
  embedded: boolean;
  // CO-3: whether to draw the dot-grid background. Defaults to on; the Settings
  // "Show dot grid" preference flows here. Ignored when embedded (exports stay
  // clean regardless).
  showGrid?: boolean;
  // WS8a locked notation: the canvas ground, so JS-set notation colors (the loop
  // cycle stroke) invert with the paper the same way the CSS-driven badge does.
  canvasPaper?: 'white' | 'yellow' | 'green' | 'black';
  project: Project;
  model: Model;
  view: StockFlowView;
  version: number;
  // D2 criterion 14: active render mode for the canvas.  Mode switching
  // is expressed at exactly one dispatch site in `getViewElements`
  // (criterion 23) -- the per-element rendering methods do not branch
  // on this prop.  When the value is `'cld'`, variable nodes route
  // through `cldVariable` and links route through `cldArrow`.
  renderMode: import('../render-mode').RenderMode;
  // D2 criterion 16: optional polarity lookup for CLD arrows, keyed by
  // `${fromIdent}→${toIdent}` (the ASCII " -> " replacement uses
  // U+2192 RIGHTWARDS ARROW for stability across hosts).  Missing
  // entries default to 'unknown' -- the CLDArrow then renders the
  // gray '?' badge.  The Editor populates this asynchronously via
  // `model.getLinks()` and re-fetches after each project version bump.
  linkPolarities?: ReadonlyMap<string, CLDArrowPolarity>;
  // D2 criterion 17: feedback loops detected by the engine, surfaced
  // to the CLD render path as R/B badges at each loop's centroid.
  // The order in this array drives the per-polarity numbering of
  // labels ("R1", "R2", "B1", ...), so callers should hand back
  // loops in a stable order (the engine already sorts by average
  // importance).  Empty / undefined renders no labels.
  loops?: ReadonlyArray<{ id: string; polarity: CLDLoopPolarity; variables: readonly string[] }>;
  // MVP WS2 (AC2.2): interactive loop badges. When these are supplied the loop
  // labels become hover/clickable; the Editor uses them to highlight the loop's
  // member variables on the canvas. `highlightedLoopIds` is the set of emphasised
  // loops -- CO-5 supports pinning several at once from the Loop Picker (plus a
  // transient hover preview). Absent/empty -> badges stay decorative.
  onLoopHover?: (id: string, hovering: boolean) => void;
  onLoopSelect?: (id: string) => void;
  highlightedLoopIds?: ReadonlySet<string>;
  // CO-5 per-loop color (Williams 2026-08-07): loop id -> badge/ring fill, so the
  // canvas and the Loop Picker color each loop identically. Absent -> fall back to
  // the polarity color (cyan/slate).
  loopColors?: ReadonlyMap<string, string>;
  // D3 criterion 28: read-only annotation overlay rendered above the
  // SFD/CLD content but inside the zoom-transformed group so it tracks
  // pan/zoom for free.  Annotations are a separate state slice -- they
  // never appear in `view.elements` and never flow through the engine
  // patch pipeline.  Missing / empty renders no overlay.
  annotations?: ReadonlyArray<Annotation>;
  // D3 criterion 29: metadata-driven variable highlights, keyed by
  // canonical ident.  Each entry picks one of three visual emphases
  // (border, glow, badge); the resolver joins the metadata against
  // the visible view elements at render time so highlights track
  // variable movement automatically.  Like annotations, this is a
  // separate state slice that never reaches `view.elements`.
  // Missing / empty renders no overlay.
  variableHighlights?: ReadonlyMap<string, VariableHighlight>;
  // D3 criterion 30: pill-shaped text badges attached to either a
  // variable (by canonical ident) or a free-floating canvas point.
  // Variable-anchored labels follow the variable through pan / zoom
  // / re-layout because the resolver re-runs each render.
  // Region-anchored labels stay at their (x, y) anchor.  Like the
  // other overlay state slices, labels never reach `view.elements`.
  annotationLabels?: ReadonlyArray<AnnotationLabel>;
  // D3 criterion 31: labeled boundary regions identifying recognized
  // patterns (archetypes, LTM loops, AI-detected clusters).  Each
  // group lists a set of variable idents; the resolver computes the
  // bounding rectangle from the visible elements and the overlay
  // paints a rounded, dashed boundary with a top-left header label.
  // Like the other overlays, pattern groups are a separate state
  // slice that never reaches `view.elements`.
  patternGroups?: ReadonlyArray<PatternGroup>;
  // D3 criterion 32: per-overlay-kind visibility flags.  Missing
  // (undefined) is treated as "all four overlays visible" so callers
  // that haven't adopted the toggle continue to render any overlay
  // data they pass.  Toggling these flags has no effect on
  // `view.elements`, the engine project, or simulation results --
  // it only short-circuits the corresponding overlay render block.
  overlayVisibility?: OverlayVisibility;
  selectedTool: 'stock' | 'flow' | 'aux' | 'link' | 'module' | undefined;
  selection: ReadonlySet<UID>;
  onRenameVariable: (oldName: string, newName: string) => void;
  onSetSelection: (selected: ReadonlySet<UID>) => void;
  onMoveSelection: (position: Point, arcPoint?: Point, segmentIndex?: number) => void;
  onMoveFlow: (
    flow: FlowViewElement,
    targetUid: number,
    moveDelta: Point,
    fauxTargetCenter: Point | undefined,
    inCreation: boolean,
    isSourceAttach?: boolean,
  ) => void;
  onMoveLabel: (uid: UID, side: 'top' | 'left' | 'bottom' | 'right') => void;
  onAttachLink: (link: LinkViewElement, newTarget: string) => void;
  onCreateVariable: (element: ViewElement) => void;
  onClearSelectedTool: () => void;
  onDeleteSelection: () => void;
  onShowVariableDetails: () => void;
  onViewBoxChange: (viewBox: ViewRect, zoom: number) => void;
  onDrillIntoModule: (moduleIdent: string, targetModelName: string) => void;
}

export class Canvas extends React.PureComponent<CanvasProps, CanvasState> {
  state: CanvasState;

  readonly svgRef: React.RefObject<HTMLDivElement | null>;

  // XXX: these should all be private, but that doesn't work with styled
  svgObserver: ResizeObserver | undefined;
  mouseDownPoint: Point | undefined;
  selectionCenterOffset: Point | undefined;
  pointerId: number | undefined;
  elementBounds: Array<Rect | undefined> = [];
  prevSelectedTool: 'stock' | 'flow' | 'aux' | 'link' | 'module' | undefined;
  // we have to regenerate selectionUpdates when selection !== props.selection
  selection: ReadonlySet<UID> = new Set<UID>();
  cachedVersion = -Infinity;
  // D2 criterion 39: the `view` reference is part of the cache key
  // alongside `cachedVersion`. Module navigation (drill-in, back,
  // navigate-to-level) swaps the active model's view without bumping
  // `projectVersion`, so version alone is insufficient -- without
  // this field, `this.elements` would retain the previous model's
  // UIDs and the canvas would render against the old map until the
  // next engine round-trip happened to change `projectVersion`. The
  // upstream `preserveLiveView` invariant guarantees a fresh view
  // reference per model switch, so reference identity is the right
  // check here.
  cachedView: StockFlowView | undefined = undefined;
  cachedElements: readonly ViewElement[] = [];
  elements = new Map<UID, ViewElement>();
  selectionUpdates = new Map<UID, ViewElement>();
  computeBounds = false;

  // Multi-touch tracking for pinch gestures
  activePointers = new Map<number, TrackedPointer>();

  // Momentum/inertia animation
  velocityTracker: VelocityTracker = { positions: [] };
  momentumAnimationId: number | undefined;
  momentumStartTime: number | undefined;
  momentumInitialVelocity: Point | undefined;
  momentumStartOffset: Point | undefined;

  // Deferred selection state for click-in-group-then-drag behavior.
  // Set on mouseDown when clicking an already-selected element without modifier;
  // resolved on mouseUp based on whether a drag occurred.
  deferredSingleSelectUid: UID | undefined;
  deferredIsText: boolean | undefined;

  // Link drag state (creation and reattachment): tracks the arc shown during
  // the last render so the persisted value on mouse-up exactly matches the
  // visual, and stores the pointer type to distinguish mouse from touch.
  draggedLinkArc: number | undefined;
  /**
   * Derived takeoff angles for links that would otherwise render
   * coincident (issue #12).  Rebuilt every render by
   * `renderInitAndCache`; consumed by `connector()`.  Never written
   * back into the view -- see `link-separation.ts`.
   */
  linkSeparation: Map<UID, number> = new Map();
  dragPointerType: string | undefined;

  // Cached per-render: whether any module in the current model has a model
  // reference. When false, warning indicators on unconfigured modules are
  // suppressed (AC1.6 -- new model scenario where user is sketching structure).
  hasAnyModuleReference = false;

  constructor(props: CanvasProps) {
    super(props);

    this.svgRef = React.createRef();

    this.state = {
      isMovingArrow: false,
      isMovingSource: false,
      isMovingCanvas: false,
      isDragSelecting: false,
      isEditingName: false,
      isMovingLabel: false,
      labelSide: undefined,
      editingName: [],
      editNameOnPointerUp: false,
      flowStillBeingCreated: false,
      dragSelectionPoint: undefined,
      moveDelta: undefined,
      movingCanvasOffset: undefined,
      initialBounds: viewRectDefault(),
      svgSize: undefined,
      inCreation: undefined,
      inCreationCloud: undefined,
      // Multi-touch pinch state
      isPinching: false,
      initialPinchDistance: 0,
      initialPinchZoom: 1,
      pinchModelPoint: undefined,
      draggingSegmentIndex: undefined,
    };
  }

  getCanvasOffset(): Readonly<Point> {
    return this.state.movingCanvasOffset ?? this.props.view.viewBox;
  }

  getElementByUid(uid: UID): ViewElement {
    let element: ViewElement | undefined;
    if (uid === inCreationUid) {
      element = this.state.inCreation;
    } else if (uid === inCreationCloudUid) {
      element = this.state.inCreationCloud;
    } else {
      element = this.elements.get(uid);
    }
    return defined(element);
  }

  /**
   * Non-throwing counterpart to `getElementByUid`. Returns `undefined`
   * for callers that need to tolerate the transient post-creation
   * window where `selection` references a UID whose corresponding
   * element hasn't yet landed in `this.elements` (the optimistic
   * setState fires before the engine round-trip rebuilds the element
   * cache). Render paths that would otherwise crash on `defined()`
   * use this and skip the affected sub-tree for the frame instead.
   */
  tryGetElementByUid(uid: UID): ViewElement | undefined {
    if (uid === inCreationUid) {
      return this.state.inCreation;
    }
    if (uid === inCreationCloudUid) {
      return this.state.inCreationCloud;
    }
    return this.elements.get(uid);
  }

  // for resolving connector ends
  static buildSelectionMap(
    props: CanvasProps,
    elements: ReadonlyMap<UID, ViewElement>,
    inCreation?: ViewElement,
  ): Map<UID, ViewElement> {
    const selection = new Map<UID, ViewElement>();
    for (const uid of props.selection) {
      if (uid === inCreationUid) {
        if (inCreation) {
          selection.set(uid, inCreation);
        }
        // When inCreation is undefined the async Editor update hasn't
        // finished yet — skip this transient UID; the next render after
        // Editor.setState will carry the real selection.
        continue;
      }
      // The selection map may also carry stale UIDs immediately after
      // a delete: Editor removes the element from view.elements and
      // then asynchronously setState's the new selection, so for one
      // render Canvas sees `props.selection` still pointing at a UID
      // whose element is gone.  Skip rather than throw -- the next
      // render after the selection setState commits picks up the
      // cleaned-up selection.  Same transient-UID rationale as the
      // inCreation case above.
      const e = elements.get(uid);
      if (e === undefined) {
        continue;
      }
      selection.set(e.uid, e);
    }
    return selection;
  }

  isSelected(element: ViewElement): boolean {
    return this.props.selection.has(element.uid);
  }

  alias(element: AliasViewElement): React.ReactElement {
    const aliasOf = this.elements.get(element.aliasOfUid) as NamedViewElement | undefined;
    let series;
    let isValidTarget: boolean | undefined;
    if (aliasOf) {
      series = this.props.model.variables.get(defined(aliasOf.ident))?.data;
      isValidTarget = this.isValidTarget(aliasOf);
    }
    const isSelected = this.isSelected(element);
    const props: AliasProps = {
      isSelected,
      isValidTarget,
      series,
      onSelection: this.handleSetSelection,
      onLabelDrag: this.handleLabelDrag,
      element,
      aliasOf,
    };
    return <Alias key={element.uid} {...props} />;
  }

  cloud(element: CloudViewElement): React.ReactElement | undefined {
    const isSelected = this.isSelected(element);

    // TODO: fix this -- we apparently can get in the state where a flow doesn't exist but we haven't deleted the cloud
    let flow: FlowViewElement;
    try {
      flow = this.getElementByUid(defined(element.flowUid)) as FlowViewElement;
    } catch {
      return;
    }

    // When dragging a cloud to attach to a stock, we need to visually hide it
    // but keep it in the DOM to maintain pointer capture.
    let isHidden = false;
    if (this.isSelected(flow)) {
      try {
        if (this.state.isMovingArrow && isCloudOnSinkSide(element, flow)) {
          isHidden = true;
        } else if (this.state.isMovingSource && isCloudOnSourceSide(element, flow)) {
          isHidden = true;
        }
      } catch (e) {
        console.error('Invalid flow state when checking cloud position:', e);
      }
    }

    const props: CloudProps = {
      element,
      isSelected,
      isHidden,
      onSelection: this.handleSetSelection,
    };

    if (this.computeBounds) {
      this.elementBounds.push(cloudBounds(element));
    }

    return <Cloud key={element.uid} {...props} />;
  }

  isValidTarget(element: ViewElement): boolean | undefined {
    const { isMovingArrow, isMovingSource } = this.state;

    if ((!isMovingArrow && !isMovingSource) || !this.selectionCenterOffset) {
      return undefined;
    }

    const arrowUid = only(this.props.selection);
    const arrow = this.getElementByUid(arrowUid);

    const off = this.selectionCenterOffset;
    const delta = this.state.moveDelta || { x: 0, y: 0 };
    const canvasOffset = this.getCanvasOffset();
    const pointer = {
      x: off.x - delta.x - canvasOffset.x,
      y: off.y - delta.y - canvasOffset.y,
    };

    let isTarget = false;
    if (element.type === 'cloud') {
      isTarget = cloudContains(element, pointer);
    } else if (element.type === 'stock') {
      isTarget = stockContains(element, pointer);
    } else if (element.type === 'module') {
      isTarget = moduleContains(element, pointer);
    } else if (element.type === 'aux') {
      isTarget = auxContains(element, pointer);
    } else if (element.type === 'flow') {
      isTarget = auxContains(element, pointer);
    }
    if (!isTarget) {
      return undefined;
    }

    // don't allow connectors from and to the same element
    if (arrow.type === 'link' && arrow.fromUid === element.uid) {
      return undefined;
    }

    // dont allow duplicate links between the same two elements
    if (arrow.type === 'link') {
      const { view } = this.props;
      for (const e of view.elements) {
        // skip if its not a connector, or if it is the currently selected connector
        if (e.type !== 'link' || e.uid === arrow.uid) {
          continue;
        }

        if (e.fromUid === arrow.fromUid && e.toUid === element.uid) {
          return false;
        }
      }
    }

    if (arrow.type === 'flow') {
      if (element.type !== 'stock') {
        return false;
      }

      if (isMovingSource) {
        // For source movement: check if target stock is valid source
        const lastPt = last(arrow.points);
        // Don't allow connecting source and sink to the same stock
        if (lastPt.attachedToUid === element.uid) {
          return false;
        }
        // For multi-segment flows (3+ points), the source needs to align with
        // the adjacent point (second), not the sink point. For 2-point flows,
        // points[1] gives us the last point, which is correct.
        const adjacentToSource = at(arrow.points, 1);
        return (
          Math.abs(adjacentToSource.x - element.x) < StockWidth / 2 ||
          Math.abs(adjacentToSource.y - element.y) < StockHeight / 2
        );
      } else {
        // For arrowhead movement: check if target stock is valid sink
        const firstPt = first(arrow.points);
        // make sure we don't point a flow back at its source
        if (firstPt.attachedToUid === element.uid) {
          return false;
        }
        // For multi-segment flows (3+ points), the arrowhead needs to align with
        // the adjacent point (second-to-last), not the source point. For 2-point
        // flows, points.length - 2 = 0 gives us the first point, which is correct.
        const adjacentToArrowhead = at(arrow.points, arrow.points.length - 2);
        return (
          Math.abs(adjacentToArrowhead.x - element.x) < StockWidth / 2 ||
          Math.abs(adjacentToArrowhead.y - element.y) < StockHeight / 2
        );
      }
    }

    return element.type === 'flow' || element.type === 'aux' || element.type === 'module';
  }

  aux(element: AuxViewElement): React.ReactElement {
    const variable = this.props.model.variables.get(element.ident);
    const hasWarning = variable ? variableHasError(variable) : false;
    const isSelected = this.isSelected(element);
    const series = variable?.data;
    const props: AuxProps = {
      element,
      series,
      isSelected,
      isEditingName: isSelected && this.state.isEditingName,
      isValidTarget: this.isValidTarget(element),
      onSelection: this.handleSetSelection,
      onLabelDrag: this.handleLabelDrag,
      hasWarning,
    };

    if (this.computeBounds) {
      this.elementBounds.push(auxBounds(element));
    }

    return <Aux key={element.uid} {...props} />;
  }

  stock(element: StockViewElement): React.ReactElement {
    const variable = this.props.model.variables.get(element.ident);
    const hasWarning = variable ? variableHasError(variable) : false;
    const isSelected = this.isSelected(element);
    const series = variable?.data;
    const props: StockProps = {
      element,
      series,
      isSelected,
      isEditingName: isSelected && this.state.isEditingName,
      isValidTarget: this.isValidTarget(element),
      onSelection: this.handleSetSelection,
      onLabelDrag: this.handleLabelDrag,
      hasWarning,
    };

    if (this.computeBounds) {
      this.elementBounds.push(stockBounds(element));
    }
    return <Stock key={element.uid} {...props} />;
  }

  module(element: ModuleViewElement) {
    const variable = this.props.model.variables.get(element.ident);
    const hasEngineError = variable ? variableHasError(variable) : false;
    // AC1.6: suppress warning when no module in the model has a model reference
    // yet (new model scenario where user is rapidly sketching structure).
    const hasWarning = hasEngineError && this.hasAnyModuleReference;
    const isSelected = this.isSelected(element);
    const props: ModuleProps = {
      element,
      isSelected,
      isEditingName: isSelected && this.state.isEditingName,
      isValidTarget: this.isValidTarget(element),
      onSelection: this.handleSetSelection,
      onLabelDrag: this.handleLabelDrag,
      onDoubleClick: this.handleModuleDoubleClick,
      hasWarning,
    };

    if (this.computeBounds) {
      this.elementBounds.push(moduleBounds(element));
    }
    return <Module key={element.uid} {...props} />;
  }

  /**
   * Render any variable-like element (stock, flow valve, aux, module) as
   * a CLD node.  Reuses the same selection / drag / target-validity
   * plumbing as the SFD methods above; the only thing that changes is
   * which component renders the shape.  Per criterion 23, this method
   * is the *only* place CLD-vs-SFD divergence lives -- the dispatch in
   * `getViewElements` chooses between this method and the per-type SFD
   * methods at a single mode switch point.
   */
  cldVariable(element: CLDNodeElement): React.ReactElement {
    const variable = this.props.model.variables.get(element.ident);
    const hasEngineError = variable ? variableHasError(variable) : false;
    // For modules the warning suppression rule from `module()` still
    // applies; the rest of the variable kinds surface warnings directly.
    const hasWarning =
      element.type === 'module' ? hasEngineError && this.hasAnyModuleReference : hasEngineError;
    const isSelected = this.isSelected(element);
    const props: CLDVariableProps = {
      element,
      isSelected,
      isEditingName: isSelected && this.state.isEditingName,
      isValidTarget: this.isValidTarget(element),
      onSelection: this.handleSetSelection,
      onLabelDrag: this.handleLabelDrag,
      hasWarning,
    };

    if (this.computeBounds) {
      // CO-5 text-only nodes: bounds track the label box (the invisible
      // hit/anchor box CLDVariable draws), not the old SFD glyph geometry,
      // so marquee selection and the auto-viewBox fit the visible text.
      const box = cldLabelBox(wrapLabel(displayName(element.name)));
      this.elementBounds.push({
        top: element.y - box.height / 2,
        left: element.x - box.width / 2,
        right: element.x + box.width / 2,
        bottom: element.y + box.height / 2,
      });
    }
    return <CLDVariable key={element.uid} {...props} />;
  }

  /**
   * CO-5 ring-arc routing: the arcPoint a CLD link should curve through so its arc
   * follows the loop's RING (Williams 2026-08-11 dynamic tangent-attachment
   * reference). Finds the smallest loop containing both endpoints (the tightest
   * ring the link closes), takes that loop's centroid from the ring positions, and
   * returns the ring point at the endpoints' angular bisector so the shared
   * Connector arc-fit reproduces the ring circle -- consecutive links then join
   * into one continuous curve. Falls back to the outward bulge for a degenerate
   * bisector (2-member loop), and undefined when there's no containing loop.
   */
  private cldOutwardArcPoint(
    fromIdent: string | undefined,
    toIdent: string | undefined,
    from: ViewElement,
    to: ViewElement,
  ): Point | undefined {
    const loops = this.props.loops;
    if (!loops || fromIdent === undefined || toIdent === undefined) {
      return undefined;
    }
    // Smallest loop containing both endpoints: the tightest ring this link closes.
    let best: readonly string[] | undefined;
    for (const loop of loops) {
      if (loop.variables.includes(fromIdent) && loop.variables.includes(toIdent)) {
        if (best === undefined || loop.variables.length < best.length) {
          best = loop.variables;
        }
      }
    }
    if (best === undefined) {
      return undefined;
    }
    // Loop centroid from the members' stored (CLD-view) positions.
    const pos = new Map<string, { x: number; y: number }>();
    for (const el of this.elements.values()) {
      const ident = (el as { ident?: unknown }).ident;
      if (typeof ident === 'string') {
        pos.set(ident, { x: el.x, y: el.y });
      }
    }
    let cx = 0;
    let cy = 0;
    let n = 0;
    for (const ident of best) {
      const p = pos.get(ident);
      if (p) {
        cx += p.x;
        cy += p.y;
        n += 1;
      }
    }
    if (n === 0) {
      return undefined;
    }
    const center = { x: cx / n, y: cy / n };
    const fromPt = { x: from.x, y: from.y };
    const toPt = { x: to.x, y: to.y };
    // A 2-member loop (bigon) has its two antiparallel links collapse onto one
    // line under the ring math (the centroid sits on the chord). Bow each to its
    // left perpendicular instead, so the pair opens into a lens (the Bass floor:
    // two bigons sharing the Adoption Rate hub read as two lenses -- Panel A).
    if (best.length === 2) {
      return perpendicularBowPoint(fromPt, toPt);
    }
    // Ring arc (tangent attachment) is the target; the outward bulge is the
    // graceful fallback when the bisector is degenerate.
    return ringArcPoint(fromPt, toPt, center) ?? outwardArcPoint(fromPt, toPt, center);
  }

  /**
   * Render a single LinkViewElement as a CLD causal arrow with a
   * polarity badge.  Looks up polarity in `this.props.linkPolarities`
   * keyed by `${fromIdent}→${toIdent}`; missing entries fall back to
   * `'unknown'` so models that have not yet had their polarities
   * fetched still render a complete arrow set (gray '?' badge).
   *
   * For C16 the drag-driven arrow-creation / sticky-target visuals
   * stay on the SFD path; CLD-mode drag uses the existing in-creation
   * logic in `connector()`.  When that path is unified for both
   * modes in a later iteration the resolution + arrowhead-target
   * handling here will mirror `connector()` more closely.
   */
  cldArrow(element: LinkViewElement): React.ReactElement | undefined {
    const updatedElement = this.selectionUpdates.get(element.uid);
    if (updatedElement !== undefined && updatedElement.type === 'link') {
      element = updatedElement;
    }
    const from = this.selectionUpdates.get(element.fromUid) || this.getElementByUid(element.fromUid);
    const to = this.selectionUpdates.get(element.toUid) || this.getElementByUid(element.toUid);
    if (!from || !to) {
      return undefined;
    }

    const isSelected = this.props.selection.has(element.uid);

    // Issue #12 (coincident reciprocal links) on the CLD path is handled in
    // two stages, below at the arcPoint block: `cldOutwardArcPoint` bows a pair
    // that shares a loop into a lens, and for a cross-loop link it cannot
    // separate (arcPoint undefined) we fall back to the SFD derived
    // takeoff-angle from `link-separation.ts`.

    // Both endpoints of a causal link are named variables (stocks,
    // flows, auxes, modules).  Resolve their canonical idents so the
    // polarity lookup matches the engine's `Link.from` / `Link.to`
    // strings.
    const fromIdent =
      'ident' in from && typeof (from as { ident?: string }).ident === 'string'
        ? (from as { ident: string }).ident
        : undefined;
    const toIdent =
      'ident' in to && typeof (to as { ident?: string }).ident === 'string'
        ? (to as { ident: string }).ident
        : undefined;

    let polarity: CLDArrowPolarity = 'unknown';
    if (fromIdent !== undefined && toIdent !== undefined && this.props.linkPolarities) {
      const key = `${fromIdent}→${toIdent}`;
      polarity = this.props.linkPolarities.get(key) ?? 'unknown';
    }

    // `from`/`to` already carry their stored CLD-view (ring) positions. The
    // connector's stored SFD `arc` angle was authored for the SFD layout; against
    // the ring geometry it degenerates `arcCircle` to a NaN-radius arc (poisoning
    // the path, arrowhead, and badge coordinates), so drop it and drive the curve
    // from a fresh arcPoint that bows the link OUTWARD from the loop centroid --
    // links arc clear of the node text ("arc outside; rounded") instead of cutting
    // across the ring interior.
    let arrowElement = { ...element, arc: undefined } as LinkViewElement;
    const arcPoint = this.cldOutwardArcPoint(fromIdent, toIdent, from, to);

    // #12 cross-loop fallback. cldOutwardArcPoint separates a reciprocal pair
    // only when one loop contains BOTH endpoints; a link joining two different
    // loops passes the union-membership filter but has no common loop, so
    // arcPoint is undefined and the pair would render coincident. Fall back to
    // the SFD #12 derived takeoff-angle (link-separation.ts), which does
    // separate it -- unless the arrowhead is being dragged, when connector()
    // owns the arc.
    if (arcPoint === undefined && !(this.state.isMovingArrow && isSelected)) {
      const derivedArc = this.linkSeparation.get(element.uid);
      if (derivedArc !== undefined) {
        arrowElement = { ...arrowElement, arc: derivedArc, isStraight: false };
      }
    }

    return (
      <CLDArrow
        key={element.uid}
        element={arrowElement}
        from={from}
        to={to}
        isSelected={isSelected}
        polarity={polarity}
        arcPoint={arcPoint}
        onSelection={this.handleSetSelection}
      />
    );
  }

  group(element: GroupViewElement) {
    const isSelected = this.isSelected(element);
    const props: GroupProps = {
      element,
      isSelected,
    };

    if (this.computeBounds) {
      this.elementBounds.push(groupBounds(element));
    }
    return <Group key={element.uid} {...props} />;
  }

  connector(element: LinkViewElement) {
    const { isMovingArrow } = this.state;
    const isSelected = this.props.selection.has(element.uid);

    // Get the updated element from selectionUpdates if available (arc was already adjusted
    // by applyGroupMovement for group selection cases)
    const updatedElement = this.selectionUpdates.get(element.uid);
    if (updatedElement !== undefined && updatedElement.type === 'link') {
      element = updatedElement;
    }

    const from = this.selectionUpdates.get(element.fromUid) || this.getElementByUid(element.fromUid);
    let to = this.selectionUpdates.get(element.toUid) || this.getElementByUid(element.toUid);
    let isSticky = false;

    // Dragging this link's arrowhead — covers both new-link creation and
    // reattaching an existing link.  Unified: straight line when not over
    // a target, dynamic arc when snapped to a valid target.
    const isDraggingLink = isMovingArrow && isSelected;
    if (isDraggingLink && this.selectionCenterOffset) {
      const validTarget = this.cachedElements.find((e: ViewElement) => {
        if (e.type !== 'aux' && e.type !== 'flow' && e.type !== 'module') {
          return false;
        }
        return this.isValidTarget(e) || false;
      });
      if (validTarget) {
        isSticky = true;
        to = validTarget;
      } else {
        const off = this.selectionCenterOffset;
        const delta = this.state.moveDelta ?? { x: 0, y: 0 };
        const canvasOffset = this.getCanvasOffset();
        to = {
          ...(to as AuxViewElement),
          x: off.x - delta.x - canvasOffset.x,
          y: off.y - delta.y - canvasOffset.y,
          isZeroRadius: true,
        };
      }

      const isTouch = this.dragPointerType === 'touch';
      if (isSticky && !isTouch) {
        const arcPt = this.getArcPoint();
        const arc = arcPt ? computeLinkCreationArc(from, to, arcPt) : undefined;
        element = { ...element, arc };
        this.draggedLinkArc = arc;
      } else {
        element = { ...element, arc: undefined };
        this.draggedLinkArc = undefined;
      }
    }

    // Issue #12: links that would otherwise be drawn coincident get a
    // derived takeoff angle.  Applied to a copy on its way to the
    // renderer and never written back to the view.  The map contains no
    // entry for a link with authored geometry or for one being dragged,
    // so both are left exactly as they were.
    if (!isDraggingLink) {
      const derivedArc = this.linkSeparation.get(element.uid);
      if (derivedArc !== undefined) {
        element = { ...element, arc: derivedArc, isStraight: false };
      }
    }

    const props: ConnectorProps = {
      element,
      from,
      to,
      isSelected,
      isDashed: to.type === 'stock',
      onSelection: this.handleEditConnector,
    };
    // When not dragging: pass arcPoint for existing arc-adjustment interactions
    // (e.g. clicking the arc mid-line to curve it). During link dragging the arc
    // is already computed on the element, so arcPoint would interfere.
    if (isSelected && !isSticky && !isDraggingLink) {
      props.arcPoint = this.getArcPoint();
    }
    // this.elementBounds = this.elementBounds.push(Connector.bounds(props));
    return <Connector key={element.uid} {...props} />;
  }

  getArcPoint(): FlowPoint | undefined {
    if (!this.selectionCenterOffset) {
      return undefined;
    }
    const off = defined(this.selectionCenterOffset);
    const delta = this.state.moveDelta ?? { x: 0, y: 0 };
    const canvasOffset = this.getCanvasOffset();
    return {
      x: off.x - delta.x - canvasOffset.x,
      y: off.y - delta.y - canvasOffset.y,
      attachedToUid: undefined,
    };
  }

  flow(element: FlowViewElement) {
    const variable = this.props.model.variables.get(element.ident);
    const hasWarning = variable ? variableHasError(variable) : false;
    const { isMovingArrow } = this.state;
    const isSelected = this.isSelected(element);
    const series = variable?.data;

    if (element.points.length < 2) {
      return;
    }

    const sourceId = first(element.points).attachedToUid;
    if (!sourceId) {
      return;
    }
    const source = this.getElementByUid(sourceId);
    if (source.type !== 'stock' && source.type !== 'cloud') {
      throw new Error('invariant broken');
    }

    const sinkId = last(element.points).attachedToUid;
    if (!sinkId) {
      return;
    }
    const sink = this.getElementByUid(sinkId);
    if (sink.type !== 'stock' && sink.type !== 'cloud') {
      throw new Error('invariant broken');
    }

    if (this.computeBounds) {
      this.elementBounds.push(flowBounds(element));
    }

    return (
      <Flow
        key={element.uid}
        element={element}
        series={series}
        source={source}
        sink={sink}
        embedded={this.props.embedded}
        isSelected={isSelected}
        hasWarning={hasWarning}
        isMovingArrow={isSelected && isMovingArrow}
        isMovingSource={isSelected && this.state.isMovingSource}
        isEditingName={isSelected && this.state.isEditingName}
        isValidTarget={this.isValidTarget(element)}
        onSelection={this.handleSetSelection}
        onLabelDrag={this.handleLabelDrag}
      />
    );
  }

  constrainFlowMovement(
    flow: FlowViewElement,
    moveDelta: Point,
  ): [FlowViewElement, readonly (StockViewElement | CloudViewElement)[]] {
    const sourceId = defined(first(flow.points).attachedToUid);
    let source = this.getElementByUid(sourceId) as StockViewElement | CloudViewElement;
    if (source.type !== 'stock' && source.type !== 'cloud') {
      throw new Error('invariant broken');
    }

    const sinkId = defined(last(flow.points).attachedToUid);
    let sink = this.getElementByUid(sinkId) as StockViewElement | CloudViewElement;
    if (sink.type !== 'stock' && sink.type !== 'cloud') {
      throw new Error('invariant broken');
    }

    const { isMovingArrow, isMovingSource } = this.state;

    if (isMovingSource && this.selectionCenterOffset) {
      // Source movement: find valid target for source end
      const validTarget = this.cachedElements.find((e: ViewElement) => {
        // Don't connect to the sink stock
        if (e.type !== 'stock' || e.uid === sinkId) {
          return false;
        }
        return this.isValidTarget(e) || false;
      }) as StockViewElement;

      if (validTarget) {
        moveDelta = {
          x: source.x - validTarget.x,
          y: source.y - validTarget.y,
        };
        source = {
          ...validTarget,
          uid: sourceId,
          x: source.x,
          y: source.y,
        };
      } else {
        const off = this.selectionCenterOffset;
        const canvasOffset = this.getCanvasOffset();

        source = {
          ...source,
          x: off.x - canvasOffset.x,
          y: off.y - canvasOffset.y,
          isZeroRadius: true,
        };
      }

      [source, flow] = UpdateCloudAndFlow(source, flow, moveDelta);
      return [flow, []];
    }

    if (isMovingArrow && this.selectionCenterOffset) {
      const validTarget = this.cachedElements.find((e: ViewElement) => {
        // connecting both the inflow + outflow of a stock to itself wouldn't make sense.
        if (e.type !== 'stock' || e.uid === sourceId) {
          return false;
        }
        return this.isValidTarget(e) || false;
      }) as StockViewElement;
      if (validTarget) {
        moveDelta = {
          x: sink.x - validTarget.x,
          y: sink.y - validTarget.y,
        };
        sink = {
          ...validTarget,
          uid: sinkId,
          x: sink.x,
          y: sink.y,
        };
      } else {
        const off = this.selectionCenterOffset;
        const canvasOffset = this.getCanvasOffset();

        sink = {
          ...sink,
          x: off.x - canvasOffset.x,
          y: off.y - canvasOffset.y,
          isZeroRadius: true,
        };
      }

      [sink, flow] = UpdateCloudAndFlow(sink, flow, moveDelta);
      return [flow, []];
    }

    const ends: readonly (StockViewElement | CloudViewElement)[] = [source, sink];
    return UpdateFlow(flow, ends, moveDelta, this.state.draggingSegmentIndex);
  }

  constrainCloudMovement(
    cloudEl: CloudViewElement,
    moveDelta: Point,
  ): [StockViewElement | CloudViewElement, FlowViewElement] {
    const flow = this.getElementByUid(defined(cloudEl.flowUid)) as FlowViewElement;
    return UpdateCloudAndFlow(cloudEl, flow, moveDelta);
  }

  constrainStockMovement(
    stockEl: StockViewElement,
    moveDelta: Point,
  ): [StockViewElement, readonly FlowViewElement[]] {
    const flows = [...stockEl.inflows, ...stockEl.outflows]
      .map((uid) => (this.selectionUpdates.get(uid) || this.getElementByUid(uid)) as FlowViewElement | undefined)
      .filter((element) => element !== undefined)
      .map((element) => defined(element));

    return UpdateStockAndFlows(stockEl, flows, moveDelta);
  }

  renderInitAndCache(): readonly ViewElement[] {
    if (!setsEqual(this.props.selection, this.selection)) {
      this.selection = this.props.selection;
    }

    let displayElements: readonly ViewElement[] = this.props.view.elements;
    if (this.state.inCreation) {
      displayElements = [...displayElements, this.state.inCreation];
    }
    if (this.state.inCreationCloud) {
      displayElements = [...displayElements, this.state.inCreationCloud];
    }

    // D2 criterion 39: invalidate when EITHER the project version OR
    // the view reference changes. Model navigation handlers swap
    // `state.modelName` (which selects a different view) without
    // touching `projectVersion`, so the version-only check used to
    // miss the model switch and the diagram would keep rendering the
    // previous model's elements. See `cachedView` declaration for
    // the full invariant.
    if (this.props.version !== this.cachedVersion || this.cachedView !== this.props.view) {
      this.elements = new Map(displayElements.map((el) => [el.uid, el]));
      this.elements.set(fauxTarget.uid, fauxTarget);
      this.elements.set(fauxCloudTarget.uid, fauxCloudTarget);
      this.cachedElements = displayElements;
      this.cachedVersion = this.props.version;
      this.cachedView = this.props.view;
    }

    this.selectionUpdates = Canvas.buildSelectionMap(this.props, this.elements, this.state.inCreation);
    if (this.state.labelSide) {
      this.selectionUpdates = mapValues(this.selectionUpdates, (el) => {
        return { ...el, labelSide: defined(this.state.labelSide) } as ViewElement;
      }) as Map<UID, ViewElement>;
    }
    if (this.state.moveDelta) {
      const moveDelta = defined(this.state.moveDelta);

      // When dragging a single link arrow (creation or reattachment),
      // suppress arcPoint so processLinks doesn't compute a rotation-based
      // arc.  connector() handles arc computation directly.
      const isDraggingLink = this.state.isMovingArrow && this.props.selection.size === 1;
      const { updatedElements } = applyGroupMovement({
        elements: this.elements.values(),
        selection: this.props.selection,
        delta: moveDelta,
        arcPoint: isDraggingLink ? undefined : this.getArcPoint(),
        segmentIndex: this.state.draggingSegmentIndex,
      });

      this.selectionUpdates = new Map([...this.selectionUpdates, ...updatedElements]);
    }

    this.linkSeparation = Canvas.computeSeparation(
      displayElements,
      this.selectionUpdates,
      (uid) => this.tryGetElementByUid(uid),
      this.props.selection,
      this.state.isMovingArrow,
    );

    return displayElements;
  }

  /**
   * CO-5(b): keep only the loop subgraph in the CLD -- loop-member nodes plus the
   * links whose BOTH endpoints are members -- so non-loop constants (and their
   * feed-in links) don't clutter the causal-loop view. Positions are NOT touched:
   * the CLD renders its own persisted view (views[1]) whose members already sit at
   * their ring positions, and dragging those persists through the normal pipeline.
   * Member idents come from the detected loops.
   */
  private filterToLoopSubgraph(elements: readonly ViewElement[]): readonly ViewElement[] {
    const identOf = (el: ViewElement): string | undefined => {
      const ident = (el as { ident?: unknown }).ident;
      return typeof ident === 'string' ? ident : undefined;
    };
    const members = new Set<string>();
    for (const loop of this.props.loops ?? []) {
      for (const v of loop.variables) {
        members.add(v);
      }
    }
    const memberUids = new Set<UID>();
    for (const el of elements) {
      const ident = identOf(el);
      if (ident !== undefined && members.has(ident)) {
        memberUids.add(el.uid);
      }
    }
    return elements.filter((el) => {
      if (el.type === 'link') {
        const link = el as LinkViewElement;
        return memberUids.has(link.fromUid) && memberUids.has(link.toUid);
      }
      const ident = identOf(el);
      return ident !== undefined && members.has(ident);
    });
  }

  /**
   * Find links that would be drawn on top of one another and give each a
   * derived takeoff angle.  Issue #12.
   *
   * Runs after `selectionUpdates` is assembled so it sees the *effective*
   * element: a link that picked up an arc from `applyGroupMovement` during
   * a group move counts as having explicit geometry and is left alone,
   * exactly as an authored arc would be.
   *
   * A link whose arrowhead is currently being dragged is excluded outright.
   * `connector()` owns that link's arc for the duration of the drag (see
   * the drag invariant in `src/diagram/CLAUDE.md`), and a derived angle
   * computed underneath it would fight the cursor.
   */
  static computeSeparation(
    displayElements: readonly ViewElement[],
    selectionUpdates: ReadonlyMap<UID, ViewElement>,
    lookupElement: (uid: UID) => ViewElement | undefined,
    selection: ReadonlySet<UID>,
    isMovingArrow: boolean,
  ): Map<UID, number> {
    const separable: SeparableLink[] = [];

    for (const raw of displayElements) {
      if (raw.type !== 'link') {
        continue;
      }
      const updated = selectionUpdates.get(raw.uid);
      const element = (updated !== undefined && updated.type === 'link' ? updated : raw) as LinkViewElement;

      if (isMovingArrow && selection.has(element.uid)) {
        continue;
      }

      const from = selectionUpdates.get(element.fromUid) || lookupElement(element.fromUid);
      const to = selectionUpdates.get(element.toUid) || lookupElement(element.toUid);
      if (!from || !to) {
        continue;
      }

      const fromVisual = getVisualCenter(from);
      const toVisual = getVisualCenter(to);
      separable.push({
        uid: element.uid,
        fromUid: element.fromUid,
        toUid: element.toUid,
        hasExplicitGeometry:
          element.arc !== undefined || (element.multiPoint !== undefined && element.multiPoint.length > 0),
        midThetaDeg: (Math.atan2(toVisual.cy - fromVisual.cy, toVisual.cx - fromVisual.cx) * 180) / Math.PI,
      });
    }

    return computeLinkSeparation(separable);
  }

  clearPointerState(clearSelection = true): void {
    this.pointerId = undefined;
    this.mouseDownPoint = undefined;
    this.selectionCenterOffset = undefined;
    this.deferredSingleSelectUid = undefined;
    this.deferredIsText = undefined;
    this.dragPointerType = undefined;
    this.draggedLinkArc = undefined;

    this.setState(pointerStateReset());

    if (clearSelection) {
      this.props.onSetSelection(new Set());
    }

    this.focusCanvas();
  }

  handlePointerCancel = (e: React.PointerEvent<SVGElement>): void => {
    if (this.props.embedded) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Remove this pointer from tracking
    this.activePointers.delete(e.pointerId);

    // Handle end of pinch gesture
    if (this.state.isPinching) {
      // When exiting pinch mode, clear all gesture state for a clean restart.
      // Continuing with a single finger after pinch leads to confusing UX.
      this.setState({
        isPinching: false,
        initialPinchDistance: 0,
        initialPinchZoom: 1,
        pinchModelPoint: undefined,
      });
      this.activePointers.clear();
      this.pointerId = undefined;
      this.mouseDownPoint = undefined;
      return;
    }

    if (this.pointerId === undefined || this.pointerId !== e.pointerId) {
      return;
    }

    const showDetails = shouldShowVariableDetails(
      this.selectionCenterOffset !== undefined,
      this.state.moveDelta,
      this.state.isMovingArrow,
      this.state.isMovingSource,
      this.state.isMovingLabel,
    );

    this.pointerId = undefined;

    // Resolve deferred selection: if user clicked an already-selected element
    // without modifier, we deferred the selection change to allow group drag.
    // Now on mouseUp, if no drag occurred, collapse to the single element.
    if (this.deferredSingleSelectUid !== undefined) {
      const didDrag =
        this.state.moveDelta !== undefined && (this.state.moveDelta.x !== 0 || this.state.moveDelta.y !== 0);
      const newSel = computeMouseUpSelection(this.deferredSingleSelectUid, didDrag);
      const wasDeferredText = this.deferredIsText;
      this.deferredSingleSelectUid = undefined;
      this.deferredIsText = undefined;
      if (newSel) {
        this.props.onSetSelection(newSel);
        if (wasDeferredText && newSel.size === 1) {
          const uid = only(newSel);
          const el = this.getElementByUid(uid);
          if (!isNamedViewElement(el)) {
            // Clouds and other non-named elements can't enter text editing
            this.selectionCenterOffset = undefined;
            this.setState(pointerStateReset());
            return;
          }
          const editingName = plainDeserialize('label', displayName(defined((el as NamedViewElement).name)));
          this.setState({
            isEditingName: true,
            editingName,
            moveDelta: undefined,
            isMovingArrow: false,
            isMovingSource: false,
          });
          this.selectionCenterOffset = undefined;
          return;
        }
      }
    }

    if (this.state.isMovingLabel && this.state.labelSide) {
      const selected = only(this.props.selection);
      this.props.onMoveLabel(selected, this.state.labelSide);
      this.clearPointerState(false);
      return;
    }

    if (this.selectionCenterOffset) {
      if (this.state.moveDelta) {
        const arcPoint = this.getArcPoint();
        const delta = this.state.moveDelta;

        if (this.state.editNameOnPointerUp) {
          let inCreation = this.state.inCreation;
          if (inCreation !== undefined && (inCreation.type === 'stock' || inCreation.type === 'aux' || inCreation.type === 'module')) {
            inCreation = {
              ...inCreation,
              x: inCreation.x - delta.x,
              y: inCreation.y - delta.y,
            };
          } else {
            throw new Error('invariant broken');
          }

          const editingName = plainDeserialize('label', displayName(defined((inCreation as NamedViewElement).name)));
          this.setState({
            isEditingName: true,
            editNameOnPointerUp: false,
            editingName,
            inCreation,
            moveDelta: undefined,
          });
          this.selectionCenterOffset = undefined;
          // we do weird one off things in this codepath, so exit early
          return;
        } else if (!this.state.isMovingArrow && !this.state.isMovingSource) {
          this.props.onMoveSelection(delta, arcPoint, this.state.draggingSegmentIndex);
        } else {
          const element = this.getElementByUid(only(this.props.selection));
          let foundInvalidTarget = false;
          const validTarget = this.cachedElements.find((e: ViewElement) => {
            const isValid = this.isValidTarget(e);
            foundInvalidTarget = foundInvalidTarget || isValid === false;
            return isValid || false;
          });
          if (element.type === 'link' && validTarget) {
            // Use the arc that was last rendered — cached by connector()
            // so the saved link matches the visual exactly.  Works for
            // both new-link creation and existing-link reattachment.
            const linkToAttach = { ...element, arc: this.draggedLinkArc };
            this.props.onAttachLink(linkToAttach, defined(validTarget.ident));
          } else if (element.type === 'flow') {
            // don't create a flow stacked on top of 2 clouds due to a misclick
            if (this.state.moveDelta.x === 0 && this.state.moveDelta.y === 0 && this.state.inCreation) {
              this.clearPointerState();
              return;
            }
            const inCreation = !!this.state.inCreation;
            const isSourceAttach = this.state.isMovingSource;
            let fauxTargetCenter: Point | undefined;
            if (element.points[1]?.attachedToUid === fauxCloudTargetUid) {
              const canvasOffset = this.getCanvasOffset();
              fauxTargetCenter = {
                x: this.selectionCenterOffset.x - canvasOffset.x,
                y: this.selectionCenterOffset.y - canvasOffset.y,
              };
            }
            // For source movement when not snapped to a valid target, compute the faux source center
            if (isSourceAttach && !validTarget) {
              const canvasOffset = this.getCanvasOffset();
              fauxTargetCenter = {
                x: this.selectionCenterOffset.x - canvasOffset.x,
                y: this.selectionCenterOffset.y - canvasOffset.y,
              };
            }
            this.props.onMoveFlow(
              element,
              validTarget ? validTarget.uid : 0,
              delta,
              fauxTargetCenter,
              inCreation,
              isSourceAttach,
            );
            if (inCreation) {
              this.setState({
                isEditingName: true,
                editingName: plainDeserialize('label', displayName(defined(element.name))),
                flowStillBeingCreated: true,
              });
            }
          } else if (!foundInvalidTarget || this.state.inCreation) {
            this.props.onDeleteSelection();
          }
        }

        this.setState({
          moveDelta: undefined,
          inCreation: undefined,
          inCreationCloud: undefined,
          isMovingArrow: false,
          isMovingSource: false,
          draggingSegmentIndex: undefined,
        });
      } else if (this.state.isMovingArrow || this.state.isMovingSource) {
        // User clicked on flow arrowhead/source (or cloud) but didn't move.
        // Clear the movement flags so the cloud reappears.
        this.setState({
          isMovingArrow: false,
          isMovingSource: false,
        });
      }
      this.selectionCenterOffset = undefined;
      if (showDetails) {
        this.props.onShowVariableDetails();
      }
      return;
    }

    if (this.state.isMovingCanvas && this.state.movingCanvasOffset) {
      const newViewBox = {
        ...this.props.view.viewBox,
        x: this.state.movingCanvasOffset.x,
        y: this.state.movingCanvasOffset.y,
      };

      this.props.onViewBoxChange(newViewBox, this.props.view.zoom);
      this.setState({ movingCanvasOffset: undefined });

      // Start momentum animation for smooth deceleration
      this.startMomentumAnimation();
    }

    if (!this.mouseDownPoint) {
      return;
    }

    // Handle drag selection
    if (this.state.isDragSelecting && this.state.dragSelectionPoint) {
      const pointA = this.mouseDownPoint;
      const pointB = this.state.dragSelectionPoint;
      const canvasOffset = this.getCanvasOffset();

      // Calculate selection rectangle bounds
      const left = Math.min(pointA.x, pointB.x) - canvasOffset.x;
      const right = Math.max(pointA.x, pointB.x) - canvasOffset.x;
      const top = Math.min(pointA.y, pointB.y) - canvasOffset.y;
      const bottom = Math.max(pointA.y, pointB.y) - canvasOffset.y;

      // Find all elements within the selection rectangle
      const selectedElements = new Set<UID>();
      for (const element of this.cachedElements) {
        // Clouds use center-point containment (no visible bounds to intersect with rect corners)
        if (element.type === 'cloud') {
          if (element.x >= left && element.x <= right && element.y >= top && element.y <= bottom) {
            selectedElements.add(element.uid);
          }
        } else if (element.type === 'aux') {
          if (
            auxContains(element, { x: left, y: top }) ||
            auxContains(element, { x: right, y: top }) ||
            auxContains(element, { x: left, y: bottom }) ||
            auxContains(element, { x: right, y: bottom }) ||
            (element.x >= left && element.x <= right && element.y >= top && element.y <= bottom)
          ) {
            selectedElements.add(element.uid);
          }
        } else if (element.type === 'stock') {
          // For stocks, check if center is within rectangle
          if (element.x >= left && element.x <= right && element.y >= top && element.y <= bottom) {
            selectedElements.add(element.uid);
          }
        } else if (element.type === 'flow') {
          // For flows, check if valve center (x, y) is within rectangle
          if (element.x >= left && element.x <= right && element.y >= top && element.y <= bottom) {
            selectedElements.add(element.uid);
          }
        } else if (element.type === 'alias' || element.type === 'module') {
          // For other named elements, check if center is within rectangle
          if (element.x >= left && element.x <= right && element.y >= top && element.y <= bottom) {
            selectedElements.add(element.uid);
          }
        }
      }

      // Update selection
      this.props.onSetSelection(selectedElements);
      this.clearPointerState(false);
      return;
    }

    const clearSelection = !this.state.isMovingCanvas;
    this.clearPointerState(clearSelection);
  };

  handleSvgResize(contentRect: { width: number; height: number }) {
    const updates = {
      svgSize: {
        width: contentRect.width,
        height: contentRect.height,
      },
    };
    const oldSize = this.state.svgSize;
    if (oldSize) {
      const dWidth = contentRect.width - oldSize.width;
      const dHeight = contentRect.height - oldSize.height;
      const canvasOffset = this.getCanvasOffset();

      const newViewBox: ViewRect = {
        x: canvasOffset.x + dWidth / 4,
        y: canvasOffset.y + dHeight / 4,
        width: contentRect.width,
        height: contentRect.height,
      };

      this.props.onViewBoxChange(newViewBox, this.props.view.zoom);
    }

    this.setState(updates);
  }

  componentWillUnmount() {
    if (this.svgObserver) {
      this.svgObserver.disconnect();
      this.svgObserver = undefined;
    }
    // Remove native event listeners for gesture prevention
    const svg = this.svgRef.current?.querySelector('svg');
    if (svg) {
      svg.removeEventListener('wheel', this.handleNativeWheel);
      svg.removeEventListener('gesturestart', this.handleGestureStart);
      svg.removeEventListener('gesturechange', this.handleGestureChange);
      svg.removeEventListener('gestureend', this.handleGestureEnd);
    }
    // Cancel any running momentum animation and clear all momentum state
    this.stopMomentumAnimation();
    // Clear velocity tracking and pointer data
    this.velocityTracker.positions = [];
    this.activePointers.clear();
    // Clear single-pointer gesture state
    this.pointerId = undefined;
    this.mouseDownPoint = undefined;
    this.selectionCenterOffset = undefined;
  }

  // Flutter-style friction simulation: calculates position at time t
  // Based on Flutter's FrictionSimulation class
  // x(t) = x0 + v0 * (friction^t - 1) / ln(friction)
  frictionPosition(velocity: number, time: number): number {
    return (velocity * (Math.pow(FRICTION_COEFFICIENT, time) - 1)) / FRICTION_LOG;
  }

  // Velocity at time t: v(t) = v0 * friction^t
  frictionVelocity(velocity: number, time: number): number {
    return velocity * Math.pow(FRICTION_COEFFICIENT, time);
  }

  // Calculate velocity from recent positions for momentum scrolling.
  // Returns zero if the pointer was stationary before release (intentional stop).
  calculateVelocity(): Point {
    const positions = this.velocityTracker.positions;
    if (positions.length < 2) {
      return { x: 0, y: 0 };
    }

    const now = window.performance.now();
    const lastPosition = positions[positions.length - 1];

    // If the pointer has been stationary for more than 40ms before release,
    // the user intentionally stopped - don't start momentum.
    // 40ms ≈ 2.5 frames at 60fps, enough to detect intentional stops
    // while still capturing quick flick-and-release gestures.
    const timeSinceLastMove = now - lastPosition.timestamp;
    if (timeSinceLastMove > 40) {
      return { x: 0, y: 0 };
    }

    // Use last 100ms of samples for velocity calculation
    const recentPositions = positions.filter((p) => now - p.timestamp < 100);

    if (recentPositions.length < 2) {
      // Fall back to last two positions
      const last = positions[positions.length - 1];
      const prev = positions[positions.length - 2];
      const dt = (last.timestamp - prev.timestamp) / 1000; // seconds
      if (dt <= 0) return { x: 0, y: 0 };
      return {
        x: (last.x - prev.x) / dt,
        y: (last.y - prev.y) / dt,
      };
    }

    // Calculate average velocity over recent samples
    const first = recentPositions[0];
    const last = recentPositions[recentPositions.length - 1];
    const dt = (last.timestamp - first.timestamp) / 1000; // seconds
    if (dt <= 0) return { x: 0, y: 0 };

    return {
      x: (last.x - first.x) / dt,
      y: (last.y - first.y) / dt,
    };
  }

  // Start momentum animation after pan release
  startMomentumAnimation = () => {
    // Cancel any existing momentum animation first (defensive)
    this.stopMomentumAnimation();

    const velocity = this.calculateVelocity();
    const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);

    // Don't start animation if velocity is at or below threshold
    if (speed <= VELOCITY_THRESHOLD) {
      return;
    }

    this.momentumInitialVelocity = velocity;
    this.momentumStartOffset = { ...this.getCanvasOffset() };
    this.momentumStartTime = window.performance.now();

    this.momentumAnimationId = window.requestAnimationFrame(this.animateMomentum);
  };

  // Animation frame callback for momentum scrolling
  animateMomentum = (timestamp: number) => {
    if (
      this.momentumStartTime === undefined ||
      this.momentumInitialVelocity === undefined ||
      this.momentumStartOffset === undefined
    ) {
      return;
    }

    const elapsed = (timestamp - this.momentumStartTime) / 1000; // seconds
    const vx = this.momentumInitialVelocity.x;
    const vy = this.momentumInitialVelocity.y;

    // Calculate current velocity
    const currentVx = this.frictionVelocity(vx, elapsed);
    const currentVy = this.frictionVelocity(vy, elapsed);
    const currentSpeed = Math.sqrt(currentVx * currentVx + currentVy * currentVy);

    // Stop when velocity drops below threshold
    if (currentSpeed < VELOCITY_THRESHOLD) {
      this.stopMomentumAnimation();
      return;
    }

    // Calculate new position using friction simulation
    // Note: We ADD the friction position because higher offset = view moves in positive direction
    // but velocity is in screen coordinates where dragging right should move view left
    const dx = this.frictionPosition(vx, elapsed);
    const dy = this.frictionPosition(vy, elapsed);

    const newOffset = {
      x: this.momentumStartOffset.x + dx,
      y: this.momentumStartOffset.y + dy,
    };

    // Update viewBox with new offset
    const newViewBox = {
      ...this.props.view.viewBox,
      x: newOffset.x,
      y: newOffset.y,
    };
    this.props.onViewBoxChange(newViewBox, this.props.view.zoom);

    // Continue animation
    this.momentumAnimationId = window.requestAnimationFrame(this.animateMomentum);
  };

  stopMomentumAnimation = () => {
    if (this.momentumAnimationId !== undefined) {
      window.cancelAnimationFrame(this.momentumAnimationId);
      this.momentumAnimationId = undefined;
    }
    this.momentumStartTime = undefined;
    this.momentumInitialVelocity = undefined;
    this.momentumStartOffset = undefined;
  };

  // Track position for velocity calculation during pan
  trackPosition = (x: number, y: number) => {
    const now = window.performance.now();
    this.velocityTracker.positions.push({ x, y, timestamp: now });

    // Keep only last 200ms of positions to avoid memory bloat
    // Only reallocate array if there's actually something to remove
    const cutoff = now - 200;
    const positions = this.velocityTracker.positions;
    if (positions.length > 0 && positions[0].timestamp <= cutoff) {
      this.velocityTracker.positions = positions.filter((p) => p.timestamp > cutoff);
    }
  };

  handleWheelPan = (e: WheelEvent): void => {
    const zoom = this.props.view.zoom;
    const viewBox = this.props.view.viewBox;

    // Convert wheel delta to canvas coordinates
    // deltaMode: 0 = pixels, 1 = lines, 2 = pages
    let deltaX = e.deltaX;
    let deltaY = e.deltaY;

    if (e.deltaMode === 1) {
      // Lines - multiply by line height (typically ~16-20px)
      deltaX *= 16;
      deltaY *= 16;
    } else if (e.deltaMode === 2) {
      // Pages - use actual viewport dimensions from DOM, not stored viewBox
      // which may be stale during resize transitions
      const viewportWidth = this.svgRef.current?.clientWidth ?? viewBox.width;
      const viewportHeight = this.svgRef.current?.clientHeight ?? viewBox.height;
      deltaX *= viewportWidth;
      deltaY *= viewportHeight;
    }

    // Scale delta by zoom level (inverse because higher zoom = smaller view area)
    deltaX /= zoom;
    deltaY /= zoom;

    const newViewBox = {
      ...viewBox,
      x: viewBox.x - deltaX,
      y: viewBox.y - deltaY,
    };

    this.props.onViewBoxChange(newViewBox, zoom);
  };

  // Native wheel event handler with { passive: false } to ensure preventDefault works.
  // React's synthetic onWheel handler is passive by default, so we must use native events.
  handleNativeWheel = (e: WheelEvent): void => {
    if (this.props.embedded) {
      return;
    }

    // Always prevent default to stop browser zoom, even at zoom limits
    e.preventDefault();

    // Stop any momentum animation when user starts interacting
    this.stopMomentumAnimation();

    // On Mac trackpads, pinch-to-zoom is reported as wheel events with ctrlKey
    if (e.ctrlKey || e.metaKey) {
      this.handleNativeWheelZoom(e);
    } else {
      this.handleWheelPan(e);
    }
  };

  // Native wheel zoom handler using exponential scaling for natural macOS feel.
  // Exponential scaling ensures symmetric behavior: zoom in 2x then out 2x returns to original.
  handleNativeWheelZoom = (e: WheelEvent): void => {
    const zoom = this.props.view.zoom;

    // Exponential scaling: deltaY of PINCH_ZOOM_DIVISOR results in 2x zoom change.
    // Negative deltaY = pinch out = zoom in, so we negate to get correct direction.
    const scale = Math.pow(2, -e.deltaY / PINCH_ZOOM_DIVISOR);
    let newZoom = zoom * scale;

    // Clamp zoom level
    newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    // Use epsilon comparison for floating point
    if (Math.abs(newZoom - zoom) < 0.0001) {
      return;
    }

    // Get cursor position in canvas coordinates
    const cursorCanvas = this.getCanvasPoint(e.clientX, e.clientY);
    const viewBox = this.props.view.viewBox;

    // Calculate the point under cursor in model coordinates
    const modelX = cursorCanvas.x - viewBox.x;
    const modelY = cursorCanvas.y - viewBox.y;

    // Calculate new offset to keep the point under cursor stable
    const newCursorCanvas = this.getCanvasPointWithZoom(e.clientX, e.clientY, newZoom);
    const newOffset = {
      x: newCursorCanvas.x - modelX,
      y: newCursorCanvas.y - modelY,
    };

    const newViewBox = {
      ...viewBox,
      x: newOffset.x,
      y: newOffset.y,
    };

    this.props.onViewBoxChange(newViewBox, newZoom);
  };

  // Safari-specific gesture events for pinch-to-zoom prevention.
  // Safari triggers these events alongside wheel events for trackpad pinch gestures.
  handleGestureStart = (e: Event): void => {
    if (this.props.embedded) {
      return;
    }
    e.preventDefault();
  };

  handleGestureChange = (e: Event): void => {
    if (this.props.embedded) {
      return;
    }
    e.preventDefault();
  };

  handleGestureEnd = (e: Event): void => {
    if (this.props.embedded) {
      return;
    }
    e.preventDefault();
  };

  // Helper to get canvas point with a specific zoom level
  getCanvasPointWithZoom(x: number, y: number, zoom: number): Point {
    if (this.svgRef.current) {
      const bounds = this.svgRef.current.getBoundingClientRect();
      x -= bounds.x;
      y -= bounds.y;
    }
    return screenToCanvasPoint(x, y, zoom);
  }

  // Calculate distance between two pointers for pinch gesture
  getPinchDistance(): number {
    const pointers = Array.from(this.activePointers.values());
    if (pointers.length < 2) {
      return 0;
    }
    const dx = pointers[1].x - pointers[0].x;
    const dy = pointers[1].y - pointers[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Get the center point between two pointers
  getPinchCenter(): Point {
    const pointers = Array.from(this.activePointers.values());
    if (pointers.length < 2) {
      return { x: 0, y: 0 };
    }
    return {
      x: (pointers[0].x + pointers[1].x) / 2,
      y: (pointers[0].y + pointers[1].y) / 2,
    };
  }

  handleModuleDoubleClick = (element: ModuleViewElement): void => {
    const variable = this.props.model.variables.get(element.ident);
    if (variable?.type !== 'module' || !variable.modelName) {
      return;
    }
    this.props.onDrillIntoModule(element.ident, variable.modelName);
  };

  handleLabelDrag = (uid: number, e: React.PointerEvent<SVGElement>) => {
    this.pointerId = e.pointerId;

    const selectionSet = new Set([uid]);
    if (!setsEqual(this.props.selection, selectionSet)) {
      this.props.onSetSelection(selectionSet);
    }

    const element = this.getElementByUid(uid);
    const delta = this.getCanvasOffset();
    const client = this.getCanvasPoint(e.clientX, e.clientY);
    const pointer = {
      x: client.x - delta.x,
      y: client.y - delta.y,
    };

    const cx = element.x;
    const cy = element.y;

    const angle = radToDeg(Math.atan2(cy - pointer.y, cx - pointer.x));

    let side: 'top' | 'left' | 'bottom' | 'right';
    if (-45 < angle && angle <= 45) {
      side = 'left';
    } else if (45 < angle && angle <= 135) {
      side = 'top';
    } else if (-135 < angle && angle <= -45) {
      side = 'bottom';
    } else {
      side = 'right';
    }

    this.setState({
      isMovingLabel: true,
      labelSide: side,
    });
  };

  handleSelectionMove(e: React.PointerEvent<SVGElement>): void {
    if (!this.selectionCenterOffset) {
      return;
    }

    const currPt = this.getCanvasPoint(e.clientX, e.clientY);

    const dx = this.selectionCenterOffset.x - currPt.x;
    const dy = this.selectionCenterOffset.y - currPt.y;

    this.setState({
      moveDelta: {
        x: dx,
        y: dy,
      } as Point | undefined,
    });
  }

  handleMovingCanvas(e: React.PointerEvent<SVGElement>): void {
    if (!this.mouseDownPoint) {
      return;
    }

    const base = this.props.view.viewBox;
    const curr = this.getCanvasPoint(e.clientX, e.clientY);

    const newOffset = {
      x: base.x + (curr.x - this.mouseDownPoint.x),
      y: base.y + (curr.y - this.mouseDownPoint.y),
    };

    // Track position for momentum calculation
    this.trackPosition(newOffset.x, newOffset.y);

    this.setState({
      isMovingCanvas: true,
      movingCanvasOffset: newOffset,
    });
  }

  handleDragSelection(e: React.PointerEvent<SVGElement>): void {
    if (!this.mouseDownPoint) {
      return;
    }

    const dragSelectionPoint = this.getCanvasPoint(e.clientX, e.clientY);

    this.setState({
      isDragSelecting: true,
      dragSelectionPoint,
    });
  }

  handlePointerMove = (e: React.PointerEvent<SVGElement>): void => {
    if (this.props.embedded) {
      return;
    }

    // Update tracked pointer position
    if (this.activePointers.has(e.pointerId)) {
      this.activePointers.set(e.pointerId, {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        timestamp: window.performance.now(),
      });
    }

    // Handle pinch gesture
    if (this.state.isPinching && this.activePointers.size >= 2) {
      this.handlePinchMove();
      return;
    }

    if (this.pointerId !== e.pointerId) {
      return;
    } else if (this.pointerId && e.pointerType === 'mouse' && e.buttons === 0) {
      this.handlePointerCancel(e);
    }

    if (this.selectionCenterOffset) {
      this.handleSelectionMove(e);
    } else if (this.state.isDragSelecting) {
      this.handleDragSelection(e);
    } else if (this.state.isMovingCanvas) {
      this.handleMovingCanvas(e);
    }
  };

  // Handle pinch-to-zoom gesture movement
  handlePinchMove = (): void => {
    if (!this.state.isPinching || !this.state.pinchModelPoint) {
      return;
    }

    const currentDistance = this.getPinchDistance();
    if (currentDistance === 0 || this.state.initialPinchDistance === 0) {
      return;
    }

    // Calculate scale factor
    const scale = currentDistance / this.state.initialPinchDistance;
    let newZoom = this.state.initialPinchZoom * scale;

    // Clamp zoom level
    newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    // Get current pinch center in screen coordinates, then convert to canvas
    // coordinates at the NEW zoom level
    const currentCenter = this.getPinchCenter();
    const currentCenterCanvas = this.getCanvasPointWithZoom(currentCenter.x, currentCenter.y, newZoom);

    // The pinchModelPoint is fixed in model space - it's the point that was
    // under the user's fingers when the pinch started. We want that same
    // model point to remain under the current screen center.
    // newOffset = currentCenterCanvas - pinchModelPoint
    const modelPoint = this.state.pinchModelPoint;
    const newOffset = {
      x: currentCenterCanvas.x - modelPoint.x,
      y: currentCenterCanvas.y - modelPoint.y,
    };

    const newViewBox = {
      ...this.props.view.viewBox,
      x: newOffset.x,
      y: newOffset.y,
    };

    this.props.onViewBoxChange(newViewBox, newZoom);
  };

  getNewVariableName(base: string): string {
    const variables = this.props.model.variables;
    if (!variables.has(canonicalize(base))) {
      return base;
    }
    for (let i = 1; i < 1024; i++) {
      const newName = `${base} ${i}`;
      if (!variables.has(canonicalize(newName))) {
        return newName;
      }
    }
    // give up
    return base;
  }

  getCanvasPoint(x: number, y: number): Point {
    if (this.svgRef.current) {
      const bounds = this.svgRef.current.getBoundingClientRect();
      x -= bounds.x;
      y -= bounds.y;
    }
    return screenToCanvasPoint(x, y, this.props.view.zoom);
  }

  handlePointerDown = (e: React.PointerEvent<SVGElement>): void => {
    if (this.props.embedded) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Stop any momentum animation when user starts interacting
    this.stopMomentumAnimation();

    // Track this pointer for multi-touch detection
    this.activePointers.set(e.pointerId, {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      timestamp: window.performance.now(),
    });

    // Check for pinch gesture (two touches)
    if (this.activePointers.size === 2 && e.pointerType === 'touch') {
      // Start pinch mode - clear all single-finger gesture state to prevent
      // simultaneous pan+pinch or drag+pinch if user adds second finger mid-gesture
      this.pointerId = undefined;
      this.mouseDownPoint = undefined;
      this.selectionCenterOffset = undefined;
      // Reset velocity tracker since pinch doesn't use momentum
      this.velocityTracker.positions = [];

      const distance = this.getPinchDistance();
      const center = this.getPinchCenter();
      const centerCanvas = this.getCanvasPoint(center.x, center.y);
      const viewBox = this.props.view.viewBox;

      // Calculate the MODEL point under the pinch center. This is the fixed
      // point in model space that should remain under the user's fingers
      // throughout the pinch gesture.
      const pinchModelPoint = {
        x: centerCanvas.x - viewBox.x,
        y: centerCanvas.y - viewBox.y,
      };

      this.setState({
        isPinching: true,
        initialPinchDistance: distance,
        initialPinchZoom: this.props.view.zoom,
        pinchModelPoint,
        isMovingCanvas: false,
        isDragSelecting: false,
        // Clear any canvas offset to prevent momentum from starting when exiting pinch
        movingCanvasOffset: undefined,
      });
      return;
    }

    // If already pinching and a third finger comes in, ignore it
    if (this.state.isPinching) {
      return;
    }

    // For non-primary touches when we already have a primary, track for potential pinch
    if (!e.isPrimary && this.pointerId !== undefined) {
      return;
    }

    const client = this.getCanvasPoint(e.clientX, e.clientY);

    const canvasOffset = this.getCanvasOffset();
    const { selectedTool } = this.props;
    if (selectedTool === 'aux' || selectedTool === 'stock' || selectedTool === 'module') {
      let inCreation: AuxViewElement | StockViewElement | ModuleViewElement;
      if (selectedTool === 'aux') {
        const name = this.getNewVariableName('New Variable');
        inCreation = {
          type: 'aux',
          uid: inCreationUid,
          var: undefined,
          x: client.x - canvasOffset.x,
          y: client.y - canvasOffset.y,
          name,
          ident: canonicalize(name),
          labelSide: 'right',
          isZeroRadius: false,
        };
      } else if (selectedTool === 'stock') {
        const name = this.getNewVariableName('New Stock');
        inCreation = {
          type: 'stock',
          uid: inCreationUid,
          var: undefined,
          x: client.x - canvasOffset.x,
          y: client.y - canvasOffset.y,
          name,
          ident: canonicalize(name),
          labelSide: 'bottom',
          isZeroRadius: false,
          inflows: [],
          outflows: [],
        };
      } else {
        const name = this.getNewVariableName('New Module');
        inCreation = {
          type: 'module',
          uid: inCreationUid,
          var: undefined,
          x: client.x - canvasOffset.x,
          y: client.y - canvasOffset.y,
          name,
          ident: canonicalize(name),
          labelSide: 'bottom',
          isZeroRadius: false,
        };
      }

      this.pointerId = e.pointerId;
      this.selectionCenterOffset = client;

      (e.target as Element).setPointerCapture(e.pointerId);

      this.setState({
        isEditingName: false,
        editNameOnPointerUp: true,
        inCreation,
        moveDelta: {
          x: 0,
          y: 0,
        },
      });
      this.props.onSetSelection(new Set([inCreation.uid]));
      return;
    }
    this.pointerId = e.pointerId;

    if (selectedTool === 'flow') {
      const canvasOffset = this.getCanvasOffset();
      const x = client.x - canvasOffset.x;
      const y = client.y - canvasOffset.y;

      const inCreationCloud: CloudViewElement = {
        type: 'cloud',
        uid: inCreationCloudUid,
        flowUid: inCreationUid,
        x,
        y,
        isZeroRadius: false,
        ident: undefined,
      };

      const name = this.getNewVariableName('New Flow');
      const inCreation: FlowViewElement = {
        type: 'flow',
        uid: inCreationUid,
        var: undefined,
        name,
        ident: canonicalize(name),
        x,
        y,
        labelSide: 'bottom',
        points: [
          { x, y, attachedToUid: inCreationCloud.uid },
          { x, y, attachedToUid: fauxCloudTarget.uid },
        ],
        isZeroRadius: false,
      };

      this.selectionCenterOffset = client;

      this.setState({
        isEditingName: false,
        isMovingArrow: true,
        inCreation,
        inCreationCloud,
        moveDelta: {
          x: 0,
          y: 0,
        },
      });
      this.props.onSetSelection(new Set([inCreation.uid]));
      return;
    }

    // onclick handlers are weird.  If we mouse down on a circle, move
    // off the circle, and mouse-up on the canvas, the canvas gets an
    // onclick.  Instead, capture where we mouse-down'd, and on mouse up
    // check if its the same.
    this.mouseDownPoint = this.getCanvasPoint(e.clientX, e.clientY);

    if (e.pointerType === 'touch' || e.shiftKey) {
      // Initialize velocity tracking for momentum
      this.velocityTracker.positions = [];
      const canvasOffset = this.getCanvasOffset();
      this.trackPosition(canvasOffset.x, canvasOffset.y);

      this.setState({
        isDragSelecting: false,
        isMovingCanvas: true,
        inCreation: undefined,
        inCreationCloud: undefined,
      });
    } else {
      // on mobile, no drag selection
      this.setState({
        isDragSelecting: true,
        isMovingCanvas: false,
        inCreation: undefined,
        inCreationCloud: undefined,
      });
    }
  };

  handleEditingEnd = (e: React.PointerEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    this.handleEditingNameDone(false);
  };

  handleEditConnector = (element: ViewElement, e: React.PointerEvent<SVGElement>, isArrowhead: boolean): void => {
    this.handleSetSelection(element, e, false, isArrowhead);
  };

  // called from handleMouseDown in elements like Aux
  handleSetSelection = (
    element: ViewElement,
    e: React.PointerEvent<SVGElement>,
    isText?: boolean,
    isArrowhead?: boolean,
    segmentIndex?: number,
    isSource?: boolean,
  ): void => {
    if (this.props.embedded) {
      return;
    }

    let isEditingName = !!isText;
    let editingName: Array<CustomElement> = [];
    let isMovingArrow = !!isArrowhead;
    let isMovingSource = !!isSource;

    this.pointerId = e.pointerId;

    // For multi-selection, use the click point as the offset
    // This ensures smooth dragging from where the user clicked
    this.selectionCenterOffset = this.getCanvasPoint(e.clientX, e.clientY);

    if (isMovingArrow) {
      this.dragPointerType = e.pointerType;
      this.draggedLinkArc = undefined;
    }

    if (!isEditingName) {
      (e.target as Element).setPointerCapture(e.pointerId);
    }

    const { selectedTool } = this.props;
    let inCreation: ViewElement | undefined;

    if (selectedTool === 'link' && isNamedViewElement(element)) {
      isEditingName = false;
      isMovingArrow = true;
      this.dragPointerType = e.pointerType;
      this.draggedLinkArc = undefined;
      inCreation = {
        type: 'link',
        uid: inCreationUid,
        fromUid: element.uid,
        toUid: fauxTarget.uid,
        arc: 0.0,
        multiPoint: undefined,
        isStraight: false,
        polarity: undefined,
        x: 0,
        y: 0,
        isZeroRadius: false,
        ident: undefined,
      };
      element = inCreation;
    } else if (selectedTool === 'flow' && element.type === 'stock') {
      isEditingName = false;
      isMovingArrow = true;
      const startPoint: FlowPoint = {
        x: element.x,
        y: element.y,
        attachedToUid: element.uid,
      };
      const endPoint: FlowPoint = {
        x: element.x,
        y: element.y,
        attachedToUid: fauxCloudTarget.uid,
      };
      const name = this.getNewVariableName('New Flow');
      inCreation = {
        type: 'flow',
        uid: inCreationUid,
        var: undefined,
        name: name,
        ident: canonicalize(name),
        x: element.x,
        y: element.y,
        labelSide: 'bottom',
        points: [startPoint, endPoint],
        isZeroRadius: false,
      };
      element = inCreation;
    } else {
      // Not a link/flow tool action -- compute selection and handle clouds
      this.props.onClearSelectedTool();

      const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;
      const { newSelection, deferSingleSelect } = computeMouseDownSelection(
        this.props.selection,
        element.uid,
        isMultiSelect,
      );

      if (deferSingleSelect !== undefined) {
        // Element is already in the selection and no modifier -- defer selection
        // change to mouseUp so that group drag works without dissolving selection
        this.deferredSingleSelectUid = deferSingleSelect;
        this.deferredIsText = isText;
        isEditingName = false;

        this.setState({
          isEditingName: false,
          editingName,
          isMovingArrow: false,
          isMovingSource: false,
          inCreation,
          moveDelta: { x: 0, y: 0 },
          draggingSegmentIndex: segmentIndex,
        });
        return;
      }

      // Cloud re-attachment only when the cloud will be the sole selection
      const willBeSoleSelection = newSelection !== undefined && newSelection.size === 1;
      if (element.type === 'cloud' && element.flowUid !== undefined && willBeSoleSelection) {
        let flow: FlowViewElement | undefined;
        try {
          const flowElement = this.getElementByUid(element.flowUid);
          if (flowElement.type === 'flow') {
            flow = flowElement;
          }
        } catch (e) {
          console.warn(`Cloud ${element.uid} references invalid flow ${element.flowUid}:`, e);
        }
        if (flow) {
          if (isCloudOnSourceSide(element, flow)) {
            isMovingSource = true;
            element = flow;
          } else if (isCloudOnSinkSide(element, flow)) {
            isMovingArrow = true;
            element = flow;
          }
        }
      }

      // Only allow editing name if single selection of a named element
      if (isEditingName && newSelection !== undefined && newSelection.size === 1) {
        const uid = only(newSelection);
        const editingElement = this.getElementByUid(uid) as NamedViewElement;
        editingName = plainDeserialize('label', displayName(defined(editingElement.name)));
      } else {
        isEditingName = false;
      }

      if (newSelection !== undefined) {
        const enteredReattachment = isMovingSource || isMovingArrow;
        this.props.onSetSelection(resolveSelectionForReattachment(newSelection, enteredReattachment, element.uid));
      }
    }

    this.setState({
      isEditingName,
      editingName,
      isMovingArrow,
      isMovingSource,
      inCreation,
      moveDelta: { x: 0, y: 0 },
      draggingSegmentIndex: segmentIndex,
    });

    if (selectedTool === 'link' || selectedTool === 'flow') {
      this.props.onSetSelection(new Set([element.uid]));
    }
  };

  handleEditingNameChange = (value: Descendant[]): void => {
    this.setState({ editingName: value });
  };

  handleEditingNameDone = (isCancel: boolean) => {
    if (!this.state.isEditingName) {
      return;
    }

    if (isCancel) {
      if (this.state.flowStillBeingCreated) {
        this.setState({
          flowStillBeingCreated: true,
        });
        this.props.onDeleteSelection();
      }
      this.clearPointerState();
      return;
    }

    const uid = only(this.props.selection);
    const element = this.getElementByUid(uid);
    const oldName = displayName(defined((element as NamedViewElement).name));
    const newName = plainSerialize(defined(this.state.editingName));

    if (uid === inCreationUid) {
      this.props.onCreateVariable({ ...element, name: newName } as ViewElement);
    } else {
      this.props.onRenameVariable(oldName, newName);
    }

    this.clearPointerState();
  };

  focusCanvas() {
    // an SVG element can't actually be focused.  Instead, blur any _other_
    // focused element.
    if (typeof document !== 'undefined' && document && document.activeElement) {
      const activeElement = document.activeElement;
      if ('blur' in activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
    }
  }

  buildLayers(displayElements: readonly ViewElement[]): React.ReactElement[][] {
    // Compute once per render whether any module has a model reference,
    // used by module() to suppress warnings when all modules are unconfigured.
    this.hasAnyModuleReference = anyModuleHasModelReference(this.props.model.variables);

    // create different layers for each of the display types so that views compose together nicely
    const zLayers = new Array(ZMax) as React.ReactElement[][];
    for (let i = 0; i < ZMax; i++) {
      zLayers[i] = [];
    }

    // D2 criterion 23: the SFD<->CLD divergence lives at exactly one
    // mode-switch point.  We resolve `isCld` once here and reuse the
    // boolean for the per-element dispatch below and the CLD-only
    // loop-label overlay pass at the tail of this method -- no
    // additional `renderMode` comparisons appear anywhere else in
    // Canvas.
    const isCld = this.props.renderMode === 'cld';

    // CO-5(b): in CLD mode render only the loop subgraph (members + member-member
    // links); positions come from the persisted CLD view, so no re-homing here.
    // Gated on the single `isCld` so the mode switch stays at one point (criterion 23).
    if (isCld && this.props.loops && this.props.loops.length > 0) {
      displayElements = this.filterToLoopSubgraph(displayElements);
    }

    for (let element of displayElements) {
      if (this.selectionUpdates.has(element.uid)) {
        element = getOrThrow(this.selectionUpdates, element.uid);
      }

      // const ZOrder = Map<'flow' | 'module' | 'stock' | 'aux' | 'link' | 'style' | 'reference' | 'cloud' | 'alias', number>([
      //   ['style', 0],
      //   ['module', 1],
      //   ['link', 2],
      //   ['flow', 3],
      //   ['cloud', 4],
      //   ['stock', 4],
      //   ['aux', 5],
      //   ['reference', 5],
      //   ['alias', 5],
      // ]);

      let zOrder = 0;
      let component: React.ReactElement | undefined;
      // CLD-mode routing for variable-like elements (C15) and links
      // (C16); clouds, aliases, and groups stay on the SFD path
      // until a later iteration decides whether a clean CLD should
      // surface them at all.  The `isCld` boolean is the one
      // permitted mode switch (declared once outside this loop --
      // see the criterion-23 comment above).
      const isCldVariableKind =
        element.type === 'aux' ||
        element.type === 'stock' ||
        element.type === 'flow' ||
        element.type === 'module';
      if (isCld && isCldVariableKind) {
        component = this.cldVariable(element as CLDNodeElement);
        zOrder = element.type === 'flow' ? 3 : 4;
      } else if (isCld && element.type === 'link') {
        component = this.cldArrow(element);
        zOrder = 2;
      } else if (isCld) {
        // D2 criterion 38: in CLD mode, SFD-only element kinds
        // (cloud, alias, group, and any future SFD-specific kind)
        // are suppressed.  A clean causal-loop diagram surfaces
        // only variable nodes (C15) and causal arrows (C16) -- no
        // infinite-source cloud puffs, no alias ghosts, no group
        // bounding rectangles bleeding through underneath the CLD
        // overlay.  The earlier code let these fall through to the
        // SFD per-type branches below, which manifested as faint
        // SFD shapes visible behind the CLD render on Linux.  This
        // catch-all is defense-in-depth so any element kind that
        // hasn't been explicitly routed through a CLD-specific
        // renderer stays hidden in CLD mode rather than leaking
        // through to the SFD path.
        continue;
      } else if (element.type === 'aux') {
        component = this.aux(element);
        zOrder = 5;
      } else if (element.type === 'link') {
        component = this.connector(element);
        zOrder = 2;
      } else if (element.type === 'stock') {
        component = this.stock(element);
        zOrder = 4;
      } else if (element.type === 'flow') {
        component = this.flow(element);
        zOrder = 3;
      } else if (element.type === 'cloud') {
        component = this.cloud(element);
        zOrder = 4;
      } else if (element.type === 'alias') {
        component = this.alias(element);
        zOrder = 5;
      } else if (element.type === 'module') {
        component = this.module(element);
        zOrder = 4;
      } else if (element.type === 'group') {
        component = this.group(element);
        zOrder = 0; // Groups render behind everything else
      }

      if (!component) {
        continue;
      }

      zLayers[zOrder].push(component);
    }

    // D2 criterion 17: in CLD mode, overlay R/B labels at each
    // feedback loop's centroid.  Computed here (rather than in the
    // dispatch loop) because loop labels are not ViewElements --
    // they're derived overlays over the per-element render.  Sits on
    // the top layer (ZMax - 1 = 6) so the badge never gets occluded.
    // Reuses the hoisted `isCld` from above -- per criterion 23 there
    // is exactly one mode-switch comparison in this method.
    if (isCld && this.props.loops && this.props.loops.length > 0) {
      // Draw the active loop as a closed cycle first, so the R/B badge sits on
      // top of it (AC2.2 -- the loop "comes alive" on hover/select).
      this.appendLoopCycle(displayElements, zLayers[ZMax - 1]);
      this.appendLoopLabels(displayElements, zLayers[ZMax - 1]);
    }

    return zLayers;
  }

  /**
   * Compute the centroid of each detected feedback loop's member
   * variables and push a CLDLoopLabel into the supplied output layer
   * for each loop with a resolvable centroid.  Per-polarity numbering
   * ("R1", "R2", "B1", ...) is derived from the order of `loops`.
   */
  private appendLoopLabels(
    displayElements: readonly ViewElement[],
    output: React.ReactElement[],
  ): void {
    const loops = this.props.loops;
    if (!loops || loops.length === 0) {
      return;
    }

    // Build a one-shot ident -> element index.  Loops reference
    // canonical variable idents (the same form `Model.getLinks()`
    // emits), so we key the lookup on the element's `ident` field.
    const byIdent = new Map<string, ViewElement>();
    for (const elem of displayElements) {
      const ident = (elem as { ident?: unknown }).ident;
      if (typeof ident === 'string') {
        byIdent.set(ident, elem);
      }
    }

    // Badge labels ("R1", "B2", ...) come from the shared numbering so the CLD
    // badges and the Loop Picker (CO-4) never disagree on which loop is which.
    // The label is keyed on loop identity, so skipping a centroid-unresolvable
    // loop below does not renumber the others.
    const labels = loopLabels(loops);
    const placed: { loop: (typeof loops)[number]; x: number; y: number }[] = [];
    for (const loop of loops) {
      // Compute centroid from resolvable members.  Two-element loops
      // (a self-reference + one var) collapse to a single centre that
      // visually overlaps the member, so we skip anything with fewer
      // than two resolved positions.
      let sumX = 0;
      let sumY = 0;
      let count = 0;
      for (const ident of loop.variables) {
        const elem = byIdent.get(ident);
        if (!elem) {
          continue;
        }
        const x = (elem as { x?: number }).x;
        const y = (elem as { x?: number; y?: number }).y;
        if (typeof x !== 'number' || typeof y !== 'number') {
          continue;
        }
        sumX += x;
        sumY += y;
        count += 1;
      }
      if (count < 2) {
        continue;
      }

      // Raw centroid, then de-collide it off the nearest member node. When a
      // loop encircles a shared hub (e.g. Installed Base) its centroid lands
      // right on that node and the badge covers the variable name -- wrong under
      // any target layout (CLD render spec line 6/11: badges read cleanly, links
      // and names clear of the badge). Push the badge radially out past the
      // nearest node's text box so it never sits on a name. A pure best-effort
      // heuristic: repelling from the single nearest node is strictly better than
      // the raw centroid; exact placement is refined against the reference SVG.
      let bx = sumX / count;
      let by = sumY / count;
      let nearestX = 0;
      let nearestY = 0;
      let nearestClear = 0;
      let nearestDist = Infinity;
      for (const ident of loop.variables) {
        const elem = byIdent.get(ident) as { x?: number; y?: number; name?: string } | undefined;
        if (!elem || typeof elem.x !== 'number' || typeof elem.y !== 'number') {
          continue;
        }
        const nameBox = cldLabelBox(wrapLabel(displayName(elem.name ?? ident)));
        // Clearance = half the text box's larger extent + badge radius + margin.
        const clear = Math.max(nameBox.width, nameBox.height) / 2 + CLD_BADGE_RADIUS + 6;
        const d = Math.hypot(bx - elem.x, by - elem.y);
        if (d < nearestDist) {
          nearestDist = d;
          nearestX = elem.x;
          nearestY = elem.y;
          nearestClear = clear;
        }
      }
      if (nearestDist < nearestClear) {
        let dx = bx - nearestX;
        let dy = by - nearestY;
        if (dx === 0 && dy === 0) {
          // Centroid exactly on the node: default push is straight up.
          dx = 0;
          dy = -1;
        }
        const len = Math.hypot(dx, dy) || 1;
        bx = nearestX + (dx / len) * nearestClear;
        by = nearestY + (dy / len) * nearestClear;
      }

      placed.push({ loop, x: bx, y: by });
    }

    // Badge-vs-badge de-collision: loops with near-identical centroids (e.g. R1
    // and B2 both encircling the same hub) stack their badges on the same spot.
    // Spread the badge positions so each reads (spec line 6: the R/B badge is
    // separate + legible). Reuses the same local overlap-removal as node spacing.
    const spread = spreadOverlaps(
      new Map(placed.map((p) => [p.loop.id, { x: p.x, y: p.y }])),
      2 * CLD_BADGE_RADIUS + 8,
    );
    for (const { loop } of placed) {
      const pos = spread.get(loop.id) ?? { x: 0, y: 0 };
      output.push(
        <CLDLoopLabel
          key={`loop-${loop.id}`}
          id={loop.id}
          polarity={loop.polarity}
          label={labels.get(loop.id) ?? loop.id}
          fill={this.props.loopColors?.get(loop.id)}
          cx={pos.x}
          cy={pos.y}
          active={this.props.highlightedLoopIds?.has(loop.id) ?? false}
          onHoverChange={this.props.onLoopHover}
          onSelect={this.props.onLoopSelect}
        />,
      );
    }
  }

  /**
   * Draw EVERY feedback loop as a closed cycle in its polarity color -- a path
   * through the members closing back to the first -- so the CLD shows all loops as
   * visible closed rings by default (CO-5), not only on hover/pin. The active
   * (hovered / picker-selected) loop is emphasised with a heavier stroke and a
   * ring on each member; the rest render as quieter closed outlines.
   */
  private appendLoopCycle(displayElements: readonly ViewElement[], output: React.ReactElement[]): void {
    const loops = this.props.loops;
    if (!loops || loops.length === 0) {
      return;
    }

    const byIdent = new Map<string, ViewElement>();
    for (const elem of displayElements) {
      const ident = (elem as { ident?: unknown }).ident;
      if (typeof ident === 'string') {
        byIdent.set(ident, elem);
      }
    }

    const activeIds = this.props.highlightedLoopIds;
    const paper = paperKind(this.props.canvasPaper);

    // Neutral rings quiet down as the loop count grows so a dense canvas doesn't
    // drown in overlapping gray rings (still visible per spec line 2, just calmer);
    // and when a loop is selected the rest recede further so the highlighted one
    // pops. "Close enough to hand-tune" at 12 loops leans on this + the highlight.
    const anyActive = (activeIds?.size ?? 0) > 0;
    const neutralOpacity = Math.max(0.12, Math.min(0.5, 3 / loops.length)) * (anyActive ? 0.55 : 1);

    for (const loop of loops) {
      // Resolve member positions in loop order; drop members without a position.
      const points: Pt[] = [];
      for (const ident of loop.variables) {
        const elem = byIdent.get(ident) as { x?: number; y?: number } | undefined;
        if (elem && typeof elem.x === 'number' && typeof elem.y === 'number') {
          points.push({ x: elem.x, y: elem.y });
        }
      }
      if (points.length < 2) {
        continue;
      }

      // Draw EVERY loop as a closed ring, always (spec line 2). The causal-link
      // arcs alone don't close a loop whose return edge is a flow's inflow/outflow
      // (e.g. Bass: adoption_rate fills Installed Base / drains Potential Adopters,
      // which aren't connector links) -- so the ring overlay is what makes the loop
      // read as closed. Neutral gray until highlighted (spec line 7); the active
      // loop lights in its rank colour. loopRingPath lands on the same smooth curve
      // the arcs trace (a lens for a 2-member bigon).
      const isActive = activeIds?.has(loop.id) ?? false;
      const color = isActive ? this.props.loopColors?.get(loop.id) ?? loopColor(loop.polarity, paper) : CLD_NEUTRAL_RING;
      const path = loopRingPath(points);

      output.push(
        <g key={`loop-cycle-${loop.id}`} className="simlin-cld-loop-cycle" pointerEvents="none">
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={isActive ? 3 : 1.5}
            strokeLinejoin="round"
            opacity={isActive ? 0.95 : neutralOpacity}
          />
        </g>,
      );
    }
  }

  /**
   * CO-5 #1: fit-and-center the diagram in the viewport. Recomputes element
   * bounds (they're only populated when `computeBounds` is set, mirroring
   * componentDidMount), then zooms to fit those bounds within the live SVG size
   * (clamped so a small diagram isn't over-magnified) and centers them. Commits
   * through the same `onViewBoxChange` path the initial auto-fit uses, so it
   * persists like any pan/zoom. Public so the Editor can trigger it on entering
   * the CLD and from a "fit to view" control.
   */
  fitToView = (): void => {
    const svgElement = this.svgRef.current;
    if (!svgElement) {
      return;
    }
    const svgWidth = svgElement.clientWidth;
    const svgHeight = svgElement.clientHeight;
    if (svgWidth === 0 || svgHeight === 0) {
      return;
    }

    // Populate elementBounds for the current elements (as the mount path does).
    this.computeBounds = true;
    this.elementBounds = [];
    this.buildLayers(this.renderInitAndCache());
    this.computeBounds = false;
    const bounds = calcViewBox(this.elementBounds);
    this.elementBounds = [];
    if (!bounds) {
      return;
    }

    const pad = 48;
    const contentWidth = bounds.right - bounds.left + 2 * pad;
    const contentHeight = bounds.bottom - bounds.top + 2 * pad;
    const diagramCx = (bounds.left + bounds.right) / 2;
    const diagramCy = (bounds.top + bounds.bottom) / 2;

    // Zoom to fit, clamped so tiny diagrams don't balloon and huge ones stay usable.
    let zoom = Math.min(svgWidth / contentWidth, svgHeight / contentHeight);
    zoom = Math.max(0.2, Math.min(zoom, 1.5));

    // Center: the content center maps to the SVG center (matches the mount math).
    const currWidth = svgWidth / zoom;
    const currHeight = svgHeight / zoom;
    const x = currWidth / 2 - diagramCx;
    const y = currHeight / 2 - diagramCy;

    this.props.onViewBoxChange({ x, y, width: svgWidth, height: svgHeight }, zoom);
  };

  componentDidMount() {
    const displayElements = this.renderInitAndCache();

    this.computeBounds = true;
    if (this.computeBounds) {
      this.elementBounds = [];
    }

    // we are ignoring the result here, because we're calling it for
    // the side effect of computing individual bounds
    this.buildLayers(displayElements);

    let initialBounds: ViewRect | undefined;
    const bounds = calcViewBox(this.elementBounds);
    if (bounds) {
      const left = Math.floor(bounds.left) - 10;
      const top = Math.floor(bounds.top) - 10;
      const width = Math.ceil(bounds.right - left) + 10;
      const height = Math.ceil(bounds.bottom - top) + 10;
      initialBounds = { x: left, y: top, width, height };
      this.setState({ initialBounds });
    }

    const svgElement = exists(this.svgRef.current);
    this.svgObserver?.disconnect();
    this.svgObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const entry = defined(entries[0]);
      const target = entry.target as HTMLDivElement;
      this.handleSvgResize({
        width: target.clientWidth,
        height: target.clientHeight,
      });
    });

    this.svgObserver.observe(svgElement);

    // Register native event listeners with { passive: false } to ensure preventDefault() works.
    // React's synthetic event handlers are passive by default for wheel events, which means
    // preventDefault() is ignored and the browser still performs its native pinch-to-zoom.
    const svg = svgElement.querySelector('svg');
    if (svg) {
      svg.addEventListener('wheel', this.handleNativeWheel, { passive: false });
      // Safari-specific gesture events for pinch-to-zoom prevention
      svg.addEventListener('gesturestart', this.handleGestureStart, { passive: false });
      svg.addEventListener('gesturechange', this.handleGestureChange, { passive: false });
      svg.addEventListener('gestureend', this.handleGestureEnd, { passive: false });
    }

    const svgWidth = svgElement.clientWidth;
    const svgHeight = svgElement.clientHeight;

    const viewBox = this.props.view.viewBox;
    let zoom = this.props.view.zoom;

    let shouldUpdate = false;
    const prevBounds = viewBox;
    if (viewBox.width === 0 || viewBox.height === 0) {
      shouldUpdate = true;
    } else if (
      viewBox.width !== svgWidth ||
      viewBox.height !== svgHeight ||
      !isFinite(viewBox.x) ||
      !isFinite(viewBox.y) ||
      !isFinite(zoom) ||
      zoom < 0.2
    ) {
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      let x = 0;
      let y = 0;

      if (!isFinite(zoom) || zoom < 0.2) {
        zoom = 1;
      }

      // on a new diagram we won't have an initial bounds, but we should
      // still set the width/height
      if (initialBounds) {
        const currWidth = svgWidth / zoom;
        const currHeight = svgHeight / zoom;

        // convert diagram bounds to cx,cy
        initialBounds = defined(initialBounds);
        const diagramCx = initialBounds.x + initialBounds.width / 2;
        const diagramCy = initialBounds.y + initialBounds.height / 2;

        if (prevBounds.width && prevBounds.height) {
          const prevWidth = prevBounds.width / zoom;
          const prevHeight = prevBounds.height / zoom;
          const prevX = isFinite(prevBounds.x) ? prevBounds.x : 0;
          const prevY = isFinite(prevBounds.y) ? prevBounds.y : 0;
          // find where cx/cy was as % of prev viewport  (e.g. .2,.3)
          const prevCx = prevX + diagramCx;
          const prevCy = prevY + diagramCy;
          // find proportional cx/cy on curr viewport  (.2 * curr.w...)
          const fractionX = prevCx / prevWidth;
          const fractionY = prevCy / prevHeight;

          // go from cx/cy on current viewport to zoom-adjusted offset
          x = fractionX * currWidth - diagramCx;
          y = fractionY * currHeight - diagramCy;
        } else {
          const viewCx = currWidth / 2;
          const viewCy = currHeight / 2;

          x = viewCx - diagramCx;
          y = viewCy - diagramCy;
        }
      }

      const newViewBox: ViewRect = { x, y, width: svgWidth, height: svgHeight };

      this.props.onViewBoxChange(newViewBox, zoom);

      this.setState({
        svgSize: {
          width: svgWidth,
          height: svgHeight,
        },
      });
    }

    this.computeBounds = false;
    this.elementBounds = [];
  }

  render() {
    const { selectedTool, embedded } = this.props;
    // Grid draws unless embedded or the Settings preference turns it off.
    const showGrid = !embedded && this.props.showGrid !== false;

    let isEditingName = this.state.isEditingName;
    if (isEditingName && selectedTool !== this.prevSelectedTool) {
      setTimeout(() => {
        this.handleEditingNameDone(false);
      });
      isEditingName = false;
    }
    this.prevSelectedTool = selectedTool;

    // phase 1: initialize some data structures we need and potentially
    // invalidate cached data structures we have
    const displayElements = this.renderInitAndCache();

    if (embedded) {
      this.computeBounds = true;
    }

    // phase 2: create React components and add them to the appropriate layer
    const zLayers = this.buildLayers(displayElements);

    let overlayClass = styles.overlay;
    let nameEditor;

    let dragRect;
    if (this.state.isDragSelecting && this.mouseDownPoint && this.state.dragSelectionPoint) {
      const pointA = this.mouseDownPoint;
      const pointB = this.state.dragSelectionPoint;
      const offset = this.getCanvasOffset();

      const x = Math.min(pointA.x, pointB.x) - offset.x;
      const y = Math.min(pointA.y, pointB.y) - offset.y;
      const w = Math.abs(pointA.x - pointB.x);
      const h = Math.abs(pointA.y - pointB.y);

      dragRect = <rect className={styles.dragRectOverlay} x={x} y={y} width={w} height={h} />;
    }

    // Defensive: during element creation (flow / stock / aux / etc.)
    // there is a one-frame window where `selection` holds the new
    // element's UID but `this.elements` has not yet been rebuilt by
    // the engine round-trip. Falling through to the throwing
    // `getElementByUid` here blanks the page (Editor's error
    // boundary catches it but the user sees a white screen until
    // they reload). Use `tryGetElementByUid` and skip the name
    // editor for the frame instead; the next render — once the new
    // element lands in `this.elements` — populates it normally.
    const editingUid =
      isEditingName && this.props.selection.size > 0 ? only(this.props.selection) : undefined;
    const editingElement =
      editingUid !== undefined
        ? (this.tryGetElementByUid(editingUid) as NamedViewElement | undefined)
        : undefined;
    if (!editingElement || editingUid === undefined) {
      overlayClass += ' ' + styles.noPointerEvents;
    } else {
      const zoom = this.props.view.zoom;
      const { rw, rh } = labelRadii(editingElement.type);
      const side = editingElement.labelSide;
      const offset = this.getCanvasOffset();
      nameEditor = (
        <EditableLabel
          uid={editingUid}
          cx={(editingElement.x + offset.x) * zoom}
          cy={(editingElement.y + offset.y) * zoom}
          side={side}
          rw={rw * zoom}
          rh={rh * zoom}
          zoom={zoom}
          value={defined(this.state.editingName)}
          onChange={this.handleEditingNameChange}
          onDone={this.handleEditingNameDone}
        />
      );
    }

    let transform;
    let viewBox: string | undefined;
    if (embedded) {
      // For embedded/export mode, always calculate tight bounds from elements.
      // The stored view.viewBox represents the editor viewport, not diagram bounds.
      const bounds = calcViewBox(this.elementBounds);
      if (bounds) {
        const left = Math.floor(bounds.left) - 10;
        const top = Math.floor(bounds.top) - 10;
        const width = Math.ceil(bounds.right - left) + 10;
        const height = Math.ceil(bounds.bottom - top) + 10;
        viewBox = `${left} ${top} ${width} ${height}`;
      }
    } else {
      const zoom = this.props.view.zoom >= 0.2 ? this.props.view.zoom : 1;
      const offset = this.getCanvasOffset();

      transform = `matrix(${zoom} 0 0 ${zoom} ${offset.x * zoom} ${offset.y * zoom})`;
    }

    const overlay = embedded ? undefined : (
      <div className={overlayClass} onPointerDown={this.handleEditingEnd}>
        {nameEditor}
      </div>
    );

    // we don't need these things anymore

    if (this.elementBounds) {
      this.elementBounds = [];
    }
    this.selectionUpdates = new Map<UID, ViewElement>();
    // n.b. we don't want to clear this.elements as thats used when handling callbacks

    return (
      <div style={{ height: '100%', width: '100%' }} ref={this.svgRef} className={`${styles.canvas} simlin-canvas`}>
        <svg
          viewBox={viewBox}
          preserveAspectRatio="xMinYMin"
          className={clsx(styles.canvas, styles.simlinCanvas, 'simlin-canvas')}
          onPointerDown={this.handlePointerDown}
          onPointerMove={this.handlePointerMove}
          onPointerCancel={this.handlePointerCancel}
          onPointerUp={this.handlePointerCancel}
        >
          <defs>
            <filter id="labelBackground" x="-50%" y="-50%" width="200%" height="200%">
              <feMorphology operator="dilate" radius="4" />
              <feGaussianBlur stdDeviation="2" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1
                          0 0 0 0 1
                          0 0 0 0 1
                          0 0 0 0.85 0"
              />
              <feComposite operator="over" in="SourceGraphic" />
            </filter>
            {/* Editor dot-grid pattern.  20-unit spacing matches the 2x design-
                token grid (8px base x 2.5).  Defined only in interactive mode
                so exported / embedded SVGs stay clean. */}
            {showGrid && (
              <pattern id="canvasGrid" x={0} y={0} width={20} height={20} patternUnits="userSpaceOnUse">
                <circle className={styles.canvasGridDot} cx={10} cy={10} r={1.2} />
              </pattern>
            )}
          </defs>
          <g transform={transform}>
            {/* Background grid rect: sits in model-coordinate space so the
                pattern pans and zooms with the diagram.  Bounded huge to
                cover any plausible pan extent at any zoom level; the
                pattern only paints the visible region so the cost is flat
                regardless of rect size.  `pointerEvents="none"` lets the
                existing click-to-deselect and drag-rect-selection logic
                keep treating clicks on empty canvas as hits on the parent
                <svg> rather than on this background. */}
            {showGrid && (
              <rect
                x={-50000}
                y={-50000}
                width={100000}
                height={100000}
                fill="url(#canvasGrid)"
                pointerEvents="none"
              />
            )}
            {zLayers}
            {dragRect}
            {isOverlayVisible(this.props.overlayVisibility, 'patternGroups') &&
            this.props.patternGroups &&
            this.props.patternGroups.length > 0 ? (
              <PatternGroupOverlay
                groups={resolvePatternGroups(this.props.patternGroups, displayElements)}
              />
            ) : null}
            {isOverlayVisible(this.props.overlayVisibility, 'variableHighlights') &&
            this.props.variableHighlights &&
            this.props.variableHighlights.size > 0 ? (
              <VariableHighlightOverlay
                highlights={resolveHighlights(this.props.variableHighlights, displayElements)}
              />
            ) : null}
            {isOverlayVisible(this.props.overlayVisibility, 'annotationLabels') &&
            this.props.annotationLabels &&
            this.props.annotationLabels.length > 0 ? (
              <AnnotationLabelOverlay
                labels={resolveLabels(this.props.annotationLabels, displayElements)}
              />
            ) : null}
            {isOverlayVisible(this.props.overlayVisibility, 'annotations') &&
            this.props.annotations &&
            this.props.annotations.length > 0 ? (
              <AnnotationOverlay annotations={this.props.annotations} />
            ) : null}
          </g>
        </svg>
        {overlay}
      </div>
    );
  }
}
